import React, { useState, useEffect, useRef } from 'react';
import { lockScroll, unlockScroll } from '../../utils/scrollbarUtils';
import api, { authHeaders } from '../../utils/api';
import { compressImageFile } from '../../utils/imageCompressor';
import refuseIcon from '../../assets/icons/common/refuse_icon.png';
import adminEmptyImgIcon from '../../assets/icons/admin/empty_img_icon.png';
import adminDeleteIcon from '../../assets/icons/admin/delete.png';
import adminDropdownIcon from '../../assets/icons/admin/dropdown_icon.png';
import bottomNavBg from '../../assets/icons/common/bottomNav.png';

const MenuEditBottomSheet = ({ isOpen, onClose, selectedLanguage = 'ko', menuItem = null, category = '', onAddMenu, onDeleteMenu, onUpdate, storeInfo = {}, onShowToast, isWowposAdd = false, wowposCategoryId = null, onRequestBulkSalesTime, onRequestBulkSoldOut }) => {
  // Translations
  const translations = {
    ko: {
      headerTitle: '메뉴 수정',
      close: '닫기',
      imageUpload: '이미지 업로드하기',
      menuNamePlaceholder: '메뉴명',
      menuPricePlaceholder: '가격',
      currency: '원'
    },
    en: {
      headerTitle: '메뉴 수정',
      close: 'Close',
      imageUpload: 'Upload Image',
      menuNamePlaceholder: 'Menu Name',
      menuPricePlaceholder: 'Price',
      currency: '원'
    },
    ja: {
      headerTitle: '메뉴 수정',
      close: '閉じる',
      imageUpload: '画像をアップロード',
      menuNamePlaceholder: 'メニュー名',
      menuPricePlaceholder: '価格',
      currency: '원'
    },
    zh: {
      headerTitle: '메뉴 수정',
      close: '关闭',
      imageUpload: '上传图片',
      menuNamePlaceholder: '菜单名称',
      menuPricePlaceholder: '价格',
      currency: '원'
    }
  };
  
  const t = translations[selectedLanguage] || translations.ko;

  // 와우포스 매장 — 메뉴 신규 추가(isWowposAdd) 와 수정 모두 이름/가격 입력 가능 정책
  const isWowposStore = storeInfo?.pos_type === 'wowpos';
  const wpEditable = isWowposAdd || isWowposStore; // 와우포스 컨텍스트 = 이름/가격 자유 입력

  // States for form - 언어별로 가져오기
  const [menuImage, setMenuImage] = useState(menuItem?.image_url || menuItem?.image || null);
  const [originalImage, setOriginalImage] = useState(menuItem?.image_url || menuItem?.image || null); // 원본 이미지 저장
  const [newImageFile, setNewImageFile] = useState(null); // 새로 선택한 이미지 파일
  const [menuName, setMenuName] = useState('');
  const [menuPrice, setMenuPrice] = useState(() => {
    const p = menuItem?.basePrice ?? menuItem?.price;
    if (p !== null && p !== undefined && p !== '') return String(p); // 0 도 보존
    return wpEditable ? '0' : '';
  });
  const [menuDescription, setMenuDescription] = useState('');
  const [menuItemCode] = useState(menuItem?.itemCode || menuItem?.menu_code || '');
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [isPriceFocused, setIsPriceFocused] = useState(false);
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);
  const fileInputRef = useRef(null);
  
  // States for tags
  const [selectedTags, setSelectedTags] = useState({
    popular: false,
    recommend: false,
    new: false,
    mostOrdered: false
  });
  
  // State for sold out
  const [isSoldOut, setIsSoldOut] = useState(false);
  const [isDraggingToggle, setIsDraggingToggle] = useState(false);
  // ※ 영수증 출력(POS 미전송) 토글은 '상품별 출력 설정' 페이지의 자동포스팅 컬럼으로 이관되어 제거됨.
  const [toggleStartX, setToggleStartX] = useState(0);
  const [togglePosition, setTogglePosition] = useState(0);
  const [aspSoldOutYn, setAspSoldOutYn] = useState(null); // ASP 품절 상태
  const [, setIsCheckingASP] = useState(false); // ASP 조회 중
  // 다음 영업일에 자동으로 다시 판매 (품절 On 시 펼쳐지는 체크박스)
  const [autoResumeNextDay, setAutoResumeNextDay] = useState(false);
  const [resumeCheckAnimating, setResumeCheckAnimating] = useState(false); // 체크박스 바운스

  // State for sales time
  const [isSalesTimeEnabled, setIsSalesTimeEnabled] = useState(false);
  const [isDraggingTimeToggle, setIsDraggingTimeToggle] = useState(false);
  const [timeToggleStartX, setTimeToggleStartX] = useState(0);
  const [timeTogglePosition, setTimeTogglePosition] = useState(0);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isStartTimeFocused, setIsStartTimeFocused] = useState(false);
  const [isEndTimeFocused, setIsEndTimeFocused] = useState(false);
  const [startTimeError, setStartTimeError] = useState(false);
  const [endTimeError, setEndTimeError] = useState(false);
  const [timeAnimationKey, setTimeAnimationKey] = useState(0);
  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);

  // 판매시간 일괄 적용 — 수정 모드에서 판매시간 변경 후 저장 시 부모(adminDashboard)에 모달 요청.
  // (모달을 이 컴포넌트 안에 두면 onUpdate 시 부모가 언마운트시켜 사라지므로 부모로 올림)
  // 메뉴 로드 시점의 판매시간 (dirty 비교용)
  const initialSalesTimeRef = useRef({ is_sales_time: false, start_time: '', end_time: '' });
  // 메뉴 로드 시점의 품절/자동해제 (dirty 비교용 — 일괄적용 판단)
  const initialSoldOutRef = useRef({ is_soldout: false, auto_resume_soldout: false });
  
  // State for option box
  const [optionsList, setOptionsList] = useState([]);
  const [nextOptionId, setNextOptionId] = useState(1); // eslint-disable-line no-unused-vars
  const [validationErrors, setValidationErrors] = useState({}); // eslint-disable-line no-unused-vars
  const [animationKey, setAnimationKey] = useState(0); // eslint-disable-line no-unused-vars
  const [isDeleteMode, setIsDeleteMode] = useState(false); // eslint-disable-line no-unused-vars
  const [choiceFocusStates, setChoiceFocusStates] = useState({});
  const [choicePriceFocusStates, setChoicePriceFocusStates] = useState({});
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  
  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // 처리 중 상태
  
  // Delete confirmation popup state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePopupAnimating, setDeletePopupAnimating] = useState(false);
  const [deletingChoices, setDeletingChoices] = useState({}); // eslint-disable-line no-unused-vars
  
  // Drag states
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  
  // Refs
  const bottomSheetRef = useRef(null);
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  
  // Tag handlers
  const handleTagToggle = (tagName) => {
    setSelectedTags(prev => ({
      ...prev,
      [tagName]: !prev[tagName]
    }));
  };
  
  // Toggle handlers
  const handleToggleStart = (e) => {
    setIsDraggingToggle(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    setToggleStartX(clientX);
  };
  
  const handleToggleMove = (e) => {
    if (!isDraggingToggle) return;
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - toggleStartX;
    
    // 토글 범위 제한 (0 ~ 20px) - 48(바디) - 24(원) - 4(여백) = 20
    let newPosition = isSoldOut ? 20 + deltaX : deltaX;
    newPosition = Math.max(0, Math.min(20, newPosition));
    setTogglePosition(newPosition);
  };
  
  const handleToggleEnd = () => {
    if (!isDraggingToggle) return;
    setIsDraggingToggle(false);
    
    // 중간 지점(10px) 기준으로 on/off 결정
    if (togglePosition > 10) {
      setIsSoldOut(true);
      setTogglePosition(20);
    } else {
      setIsSoldOut(false);
      setTogglePosition(0);
    }
  };
  
  const handleToggleClick = () => {
    const newSoldOutState = !isSoldOut;

    // 품절 해제 시도 (품절 → 판매)
    if (newSoldOutState === false) {
      // OKDC 매장인 경우 ASP 상태 확인
      if (storeInfo.pos_type === 'okdc' && aspSoldOutYn === 'Y') {
        // ASP에서 품절 중 → 즉시 거부
        if (onShowToast) {
          onShowToast(`<img src="${refuseIcon}" style="width: 12px; height: 12px; vertical-align: middle; margin-right: 8px;" alt="x"/><span style="color: white; font-weight: 600; font-size: 14px;">POS에서 먼저 품절을 해제해주세요</span>`);
        }
        return;
      }
    }

    // ASP 판매 중이거나 품절 설정인 경우 → 즉시 변경
    setIsSoldOut(newSoldOutState);
    setTogglePosition(newSoldOutState ? 20 : 0);
  };

  // Sales time toggle handlers
  const handleTimeToggleStart = (e) => {
    setIsDraggingTimeToggle(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    setTimeToggleStartX(clientX);
  };
  
  const handleTimeToggleMove = (e) => {
    if (!isDraggingTimeToggle) return;
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - timeToggleStartX;
    
    let newPosition = isSalesTimeEnabled ? 20 + deltaX : deltaX;
    newPosition = Math.max(0, Math.min(20, newPosition));
    setTimeTogglePosition(newPosition);
  };
  
  const handleTimeToggleEnd = () => {
    if (!isDraggingTimeToggle) return;
    setIsDraggingTimeToggle(false);
    
    if (timeTogglePosition > 10) {
      setIsSalesTimeEnabled(true);
      setTimeTogglePosition(20);
    } else {
      setIsSalesTimeEnabled(false);
      setTimeTogglePosition(0);
    }
  };
  
  const handleTimeToggleClick = () => {
    setIsSalesTimeEnabled(!isSalesTimeEnabled);
    setTimeTogglePosition(isSalesTimeEnabled ? 0 : 20);
  };
  
  // Time input formatter
  const formatTimeInput = (value) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}:${numbers.slice(2)}`;
    }
    return `${numbers.slice(0, 2)}:${numbers.slice(2, 4)}`;
  };
  
  /**
   * 와우포스 메뉴 신규 추가 — 임시 객체만 부모(menuReorder) 에 전달.
   * DB INSERT 는 menuReorder 의 "저장" 버튼에서 일괄. "닫기" 누르면 폐기.
   */
  const handleWowposAddSave = () => {
    const trimmed = (menuName || '').trim();
    const errors = {};
    let hasErrors = false;

    if (!trimmed) {
      errors['menu-name'] = true;
      hasErrors = true;
    }

    // 옵션 검증 — 옵션명/선택지명 미입력 시 저장 차단 (수정 흐름과 동일 규칙)
    const updatedOptionsList = [...optionsList];
    let expandChanged = false;
    optionsList.forEach((option, optionIndex) => {
      if (option._pendingDelete) return;
      let hasThisOptionError = false;
      if (!(option.name || '').trim()) {
        errors[`option-${option.id}-name`] = true;
        hasErrors = true;
        hasThisOptionError = true;
      }
      (option.choices || []).forEach((choice, idx) => {
        if (!(choice.name || '').trim()) {
          errors[`option-${option.id}-choice-${idx}-name`] = true;
          hasErrors = true;
          hasThisOptionError = true;
        }
      });
      if (hasThisOptionError && !option.isExpanded) {
        updatedOptionsList[optionIndex].isExpanded = true;
        expandChanged = true;
      }
    });

    // 판매시간 검증 — 토글 ON인데 입력 미완료/형식 오류 시 저장 차단 (수정 흐름과 동일)
    let timeHasError = false;
    if (isSalesTimeEnabled) {
      if (!startTime || startTime.length < 5 || !validateTime(startTime)) {
        setStartTimeError(true);
        timeHasError = true;
      }
      if (!endTime || endTime.length < 5 || !validateTime(endTime)) {
        setEndTimeError(true);
        timeHasError = true;
      }
      if (timeHasError) {
        hasErrors = true;
        setTimeAnimationKey((prev) => prev + 1);
      }
    }

    if (hasErrors) {
      if (expandChanged) setOptionsList(updatedOptionsList);
      setValidationErrors((prev) => ({ ...prev, ...errors }));
      setAnimationKey((prev) => prev + 1);
      const hasOptionErr = Object.keys(errors).some((k) => k.startsWith('option-'));
      const msg = errors['menu-name']
        ? '메뉴 이름을 입력해주세요'
        : hasOptionErr
          ? '옵션 정보를 확인해주세요'
          : '판매 시간을 입력해주세요';
      onShowToast && onShowToast(msg);
      return;
    }

    // 임시 객체 — menuReorder 가 items state 에 append 해서 표시.
    // 저장 시 _wowposTemp=true 인 항목을 POST /api/wowpos/menus 로 INSERT.
    // 이미지는 File 객체 그대로 보관 → 저장 시 업로드.
    const tempMenu = {
      _wowposTemp: true,
      _tempId: `wptemp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      _imageFile: newImageFile || null,
      // menuReorder 표시용 표준 필드
      id: null,
      name: trimmed,
      name_ko: trimmed,
      is_hidden: false,
      image_url: menuImage || null, // 미리보기용 (blob URL 가능)
      // 저장 시 사용할 필드
      description_ko: (menuDescription || '').trim() || null,
      price: parseInt(String(menuPrice).replace(/[^0-9]/g, '')) || 0,
      is_popular: !!selectedTags.popular,
      is_recommend: !!selectedTags.recommend,
      is_new: !!selectedTags.new,
      is_most_ordered: !!selectedTags.mostOrdered,
      is_soldout: !!isSoldOut,
      auto_resume_soldout: isSoldOut ? !!autoResumeNextDay : false,
      is_sales_time: !!isSalesTimeEnabled,
      start_time: isSalesTimeEnabled ? startTime : null,
      end_time: isSalesTimeEnabled ? endTime : null,
    };

    // 옵션 payload 포함 (WowPos 옵션)
    tempMenu.options = buildWowposOptionsPayload();

    if (typeof onUpdate === 'function') {
      try { onUpdate(tempMenu); } catch (_) { /* noop */ }
    }
    setTimeout(() => { onClose && onClose(); }, 200);
  };

  // === WowPos 옵션 그룹/선택지 편집 핸들러 (OKDC/Union 은 POS 동기화라 추가/삭제 불가) ===
  const handleAddOptionGroup = () => {
    const now = Date.now();
    setOptionsList((prev) => [...prev, {
      id: `new-opt-${now}`,
      name: '',
      isRequired: false,       // false=다중선택(체크박스), true=단일선택(라디오, 필수)
      togglePosition: 0,
      isExpanded: true,
      isNameFocused: false,
      // 그룹 생성 시 선택지 1개 자동 생성 (선택지 없는 빈 그룹 방지)
      choices: [{ id: `new-ch-${now}-${Math.random().toString(36).slice(2, 5)}`, name: '', price: '' }],
      _isNew: true,
    }]);
  };
  const handleDeleteOptionGroup = (optId) => {
    // 즉시 제거하지 않고 삭제 예정(_pendingDelete) 토글 — 저장 시 반영, 취소 시 미반영.
    // 단, 방금 추가한(아직 저장 안 된) 그룹은 바로 제거.
    setOptionsList((prev) => prev.flatMap((o) => {
      if (o.id !== optId) return [o];
      if (o._isNew) return []; // 신규 미저장 그룹은 즉시 제거
      return [{ ...o, _pendingDelete: !o._pendingDelete }];
    }));
  };
  const handleAddChoice = (optId) => {
    setOptionsList((prev) => prev.map((o) => o.id === optId
      ? { ...o, choices: [...o.choices, { id: `new-ch-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`, name: '', price: '' }] }
      : o));
  };
  const handleDeleteChoice = (optId, choiceId) => {
    // 마지막 선택지는 삭제 불가 — 그룹에 최소 1개는 남아야 한다.
    const grp = (optionsList || []).find((o) => o.id === optId);
    if (grp && (grp.choices || []).length <= 1) {
      alert('적어도 1개의 옵션 선택지가 있어야합니다.');
      return;
    }
    setOptionsList((prev) => prev.map((o) => o.id === optId
      ? { ...o, choices: o.choices.filter((c) => c.id !== choiceId) }
      : o));
  };

  // optionsList → 백엔드 저장 형태 (group_name_ko/en/ja/zh, choices[option_name_*, additional_price]).
  // 한국어 편집 세션에서는 name 이 ko 원본. 번역은 별도 PUT (아래 저장 로직) 으로 덮어씀.
  const buildWowposOptionsPayload = () => {
    // 한국어 세션 저장용. name=한국어. 번역(_en/_ja/_zh)은 로드 시 보존해둔 값을 함께 실어
    // delete+recreate 후에도 번역이 유지되도록 한다.
    return (optionsList || [])
      .filter((o) => !o._pendingDelete && (o.name || '').trim())
      .map((o) => ({
        group_name_ko: (o.name || '').trim(),
        group_name_en: o._trGroup?.en || null,
        group_name_ja: o._trGroup?.ja || null,
        group_name_zh: o._trGroup?.zh || null,
        is_required: !!o.isRequired,
        max_select: o.isRequired ? 1 : 99,
        choices: (o.choices || [])
          .filter((c) => (c.name || '').trim())
          .map((c) => ({
            option_name_ko: (c.name || '').trim(),
            option_name_en: c._trChoice?.en || null,
            option_name_ja: c._trChoice?.ja || null,
            option_name_zh: c._trChoice?.zh || null,
            additional_price: Number(String(c.price || '0').replace(/[^0-9]/g, '')) || 0,
          })),
      }));
  };

  // Time validation
  const validateTime = (timeStr) => {
    if (!timeStr || timeStr.length < 5) return true; // Don't validate incomplete input
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60;
  };
  
  const handleStartTimeChange = (e) => {
    const input = e.target;
    const cursorPos = input.selectionStart;
    const prevValue = startTime;
    const formatted = formatTimeInput(input.value);
    setStartTime(formatted);

    // 커서 위치 복원
    setTimeout(() => {
      if (input === document.activeElement) {
        // 콜론 위치 고려한 커서 위치 계산
        let newCursorPos = cursorPos;

        // 콜론을 추가한 경우 커서 위치 조정
        if (formatted.includes(':') && !prevValue.includes(':')) {
          if (cursorPos > 2) newCursorPos = cursorPos + 1;
        }
        // 콜론을 제거한 경우 커서 위치 조정
        else if (!formatted.includes(':') && prevValue.includes(':')) {
          if (cursorPos > 2) newCursorPos = cursorPos - 1;
        }

        // 백스페이스로 콜론을 지운 경우
        if (prevValue.length > formatted.length && cursorPos === 3 && prevValue[2] === ':') {
          newCursorPos = 2;
        }

        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);

    // Validate time
    if (formatted.length === 5) {
      const isValid = validateTime(formatted);
      setStartTimeError(!isValid);
      if (!isValid) {
        setTimeAnimationKey(prev => prev + 1);
      }

      // Auto-focus to end time when start time is complete and valid
      if (isValid && !endTime) {
        endTimeRef.current?.focus();
      }
    } else {
      setStartTimeError(false);
    }
  };

  const handleEndTimeChange = (e) => {
    const input = e.target;
    const cursorPos = input.selectionStart;
    const prevValue = endTime;
    const formatted = formatTimeInput(input.value);
    setEndTime(formatted);

    // 커서 위치 복원
    setTimeout(() => {
      if (input === document.activeElement) {
        // 콜론 위치 고려한 커서 위치 계산
        let newCursorPos = cursorPos;

        // 콜론을 추가한 경우 커서 위치 조정
        if (formatted.includes(':') && !prevValue.includes(':')) {
          if (cursorPos > 2) newCursorPos = cursorPos + 1;
        }
        // 콜론을 제거한 경우 커서 위치 조정
        else if (!formatted.includes(':') && prevValue.includes(':')) {
          if (cursorPos > 2) newCursorPos = cursorPos - 1;
        }

        // 백스페이스로 콜론을 지운 경우
        if (prevValue.length > formatted.length && cursorPos === 3 && prevValue[2] === ':') {
          newCursorPos = 2;
        }

        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);

    // Validate time
    if (formatted.length === 5) {
      const isValid = validateTime(formatted);
      setEndTimeError(!isValid);
      if (!isValid) {
        setTimeAnimationKey(prev => prev + 1);
      }

      // Auto-focus to start time when end time is complete and start time is empty
      if (isValid && !startTime) {
        startTimeRef.current?.focus();
      }
      // Blur when end time is complete and start time is also complete
      else if (isValid && startTime) {
        endTimeRef.current?.blur();
      }
    } else {
      setEndTimeError(false);
    }
  };
  
  // Drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStartY(e.clientY);
  };
  
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setDragStartY(e.touches[0].clientY);
  };
  
  // Toggle drag event listeners
  useEffect(() => {
    if (isDraggingToggle) {
      const handleGlobalMove = (e) => {
        handleToggleMove(e);
      };
      
      const handleGlobalEnd = () => {
        handleToggleEnd();
      };
      
      window.addEventListener('mousemove', handleGlobalMove);
      window.addEventListener('mouseup', handleGlobalEnd);
      window.addEventListener('touchmove', handleGlobalMove);
      window.addEventListener('touchend', handleGlobalEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleGlobalMove);
        window.removeEventListener('mouseup', handleGlobalEnd);
        window.removeEventListener('touchmove', handleGlobalMove);
        window.removeEventListener('touchend', handleGlobalEnd);
      };
    }
  }, [isDraggingToggle, toggleStartX, togglePosition, isSoldOut]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Sync toggle position with isSoldOut state
  useEffect(() => {
    setTogglePosition(isSoldOut ? 20 : 0);
  }, [isSoldOut]);

  // Detect keyboard visibility on mobile
  useEffect(() => {
    const handleResize = () => {
      // On mobile, viewport height changes when keyboard appears
      if (window.visualViewport) {
        const hasKeyboard = window.visualViewport.height < window.screen.height * 0.75;
        setIsKeyboardVisible(hasKeyboard);
      } else {
        // Fallback for browsers without visualViewport
        const windowHeight = window.innerHeight;
        const hasKeyboard = windowHeight < window.screen.height * 0.75;
        setIsKeyboardVisible(hasKeyboard);
      }
    };

    if (isOpen) {
      // Listen for viewport changes
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize);
      } else {
        window.addEventListener('resize', handleResize);
      }
      
      return () => {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleResize);
        } else {
          window.removeEventListener('resize', handleResize);
        }
      };
    }
  }, [isOpen]);
  
  // Sales time toggle drag event listeners
  useEffect(() => {
    if (isDraggingTimeToggle) {
      const handleGlobalMove = (e) => {
        handleTimeToggleMove(e);
      };
      
      const handleGlobalEnd = () => {
        handleTimeToggleEnd();
      };
      
      window.addEventListener('mousemove', handleGlobalMove);
      window.addEventListener('mouseup', handleGlobalEnd);
      window.addEventListener('touchmove', handleGlobalMove);
      window.addEventListener('touchend', handleGlobalEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleGlobalMove);
        window.removeEventListener('mouseup', handleGlobalEnd);
        window.removeEventListener('touchmove', handleGlobalMove);
        window.removeEventListener('touchend', handleGlobalEnd);
      };
    }
  }, [isDraggingTimeToggle, timeToggleStartX, timeTogglePosition, isSalesTimeEnabled]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Sync time toggle position with isSalesTimeEnabled state
  useEffect(() => {
    setTimeTogglePosition(isSalesTimeEnabled ? 20 : 0);
  }, [isSalesTimeEnabled]);
  
  
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (!isDragging) return;
      const deltaY = e.clientY - dragStartY;
      if (deltaY > 0) {
        setCurrentY(deltaY);
      }
    };
    
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        if (currentY > 100) {
          // 화면 밖으로 완전히 내려가는 애니메이션
          setCurrentY(window.innerHeight);
          setTimeout(() => {
            onClose();
            // setCurrentY(0) 제거 - onClose 후에는 리셋 불필요
          }, 200);
        } else {
          // 원위치로 부드럽게 복귀
          setCurrentY(0);
        }
      }
    };
    
    const handleGlobalTouchMove = (e) => {
      if (!isDragging) return;
      const deltaY = e.touches[0].clientY - dragStartY;
      if (deltaY > 0) {
        setCurrentY(deltaY);
      }
    };
    
    const handleGlobalTouchEnd = () => {
      if (isDragging) {
        setIsDragging(false);
        if (currentY > 100) {
          // 화면 밖으로 완전히 내려가는 애니메이션
          setCurrentY(window.innerHeight);
          setTimeout(() => {
            onClose();
            // setCurrentY(0) 제거 - onClose 후에는 리셋 불필요
          }, 200);
        } else {
          // 원위치로 부드럽게 복귀
          setCurrentY(0);
        }
      }
    };
    
    if (isDragging) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
      window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      window.addEventListener('touchend', handleGlobalTouchEnd, { passive: false });
      
      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalMouseUp);
        window.removeEventListener('touchmove', handleGlobalTouchMove);
        window.removeEventListener('touchend', handleGlobalTouchEnd);
      };
    }
  }, [isDragging, dragStartY, currentY]); // eslint-disable-line react-hooks/exhaustive-deps
  
  
  useEffect(() => {
    if (isOpen) {
      // 스크롤 위치 저장 (lockScroll 전에)
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
      console.log('MenuEditBottomSheet opening, current scroll:', currentScrollY);
      console.log('전달받은 category:', category);
      console.log('전달받은 menuItem:', menuItem);
      console.log('전달받은 storeInfo:', storeInfo);
      console.log('전달받은 storeInfo.pos_type:', storeInfo.pos_type);
      console.log('전달받은 onAddMenu:', typeof onAddMenu);
      
      // 새 메뉴 추가 모드일 때만 초기화
      if (!menuItem) {
        setMenuImage(null);
        setOriginalImage(null);
        setNewImageFile(null);
        setMenuName('');
        setMenuPrice('');
        setMenuDescription('');
        setSelectedTags({
          popular: false,
          recommend: false,
          new: false
        });
        setIsSoldOut(false);
        setTogglePosition(0);
        setIsSalesTimeEnabled(false);
        setTimeTogglePosition(0);
        setStartTime('');
        setEndTime('');
        setOptionsList([]);
        setValidationErrors({});
        setIsDeleteMode(false);
        setChoiceFocusStates({});
        setIsNameFocused(false);
        setIsPriceFocused(false);
        setIsDescriptionFocused(false);
        setIsStartTimeFocused(false);
        setIsEndTimeFocused(false);
      } else {
        // 기존 메뉴 수정 모드 - 언어별 데이터 사용
        // 현재 언어에 맞는 데이터 로드

        if (menuItem?.[selectedLanguage]) {
          setMenuName(menuItem[selectedLanguage].name || '');
          setMenuDescription(menuItem[selectedLanguage].description || '');
        } else {
          setMenuName(menuItem?.name || menuItem?.ko?.name || '');
          setMenuDescription(menuItem?.description || menuItem?.ko?.description || '');
        }

        // 기타 메뉴 정보 설정 (언어 변경 시에는 이미지 원본 유지)
        const imageUrl = menuItem?.image_url || menuItem?.image || null;
        setMenuImage(imageUrl);
        // originalImage는 변경하지 않음 (초기 설정 시에만 설정)
        setMenuPrice(menuItem?.basePrice || menuItem?.price || '');
        setIsSoldOut(menuItem?.soldOut || false);
        setTogglePosition(menuItem?.soldOut ? 30 : 0);
      }
      
      setIsAnimating(false);
      setIsVisible(true);
      setCurrentY(0); // 열릴 때 위치 초기화
      
      // 애니메이션 시작을 약간 지연
      setTimeout(() => {
        setIsAnimating(true);
      }, 50);
      
      // body 스크롤 방지
      console.log('lockScroll 호출 전, body.dataset.scrollY:', document.body.dataset.scrollY);
      lockScroll();
      console.log('After lockScroll, scroll:', window.pageYOffset, 'body.dataset.scrollY:', document.body.dataset.scrollY);
      
      // HTML과 body에 overscroll-behavior 추가
      document.documentElement.style.overscrollBehavior = 'none';
      document.body.style.overscrollBehavior = 'none';
      
      // 전체 document에서 pull-to-refresh 방지
      const preventPullToRefresh = (e) => {
        // 바텀시트가 열려있을 때 모든 터치무브 방지
        if (e.touches && e.touches.length === 1) {
          // content 영역에서의 스크롤만 허용
          const isContentTouch = e.target.closest('.content');
          if (!isContentTouch) {
            e.preventDefault();
            return;
          }
          
          // content 영역이라도 맨 위에서 pull하면 방지
          const content = contentRef.current;
          if (content && content.scrollTop <= 0) {
            const touch = e.touches[0];
            if (!content.dataset.touchStartY) {
              content.dataset.touchStartY = touch.clientY;
            }
            const deltaY = touch.clientY - parseFloat(content.dataset.touchStartY);
            if (deltaY > 0) {
              e.preventDefault();
            }
          }
        }
      };
      
      const handleTouchEnd = () => {
        const content = contentRef.current;
        if (content) {
          delete content.dataset.touchStartY;
        }
      };
      
      // document 레벨에서 이벤트 차단
      document.addEventListener('touchmove', preventPullToRefresh, { passive: false });
      document.addEventListener('touchend', handleTouchEnd, { passive: true });
      
      window.history.pushState({ menuEdit: true }, '');
      
      setTimeout(() => {
        setIsAnimating(true);
        console.log('Animation started, scroll:', window.pageYOffset);
      }, 10);
      
      return () => {
        document.removeEventListener('touchmove', preventPullToRefresh);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    } else {
      setIsAnimating(false);

      setTimeout(() => {
        setIsVisible(false);
        setCurrentY(0); // 닫힐 때도 위치 초기화
        // 유효성 검사 에러 상태 초기화
        setValidationErrors({});
        setStartTimeError(false);
        setEndTimeError(false);
        // 애니메이션이 완료된 후 body 스크롤 복원
        console.log('unlockScroll 호출 전, body.dataset.scrollY:', document.body.dataset.scrollY);
        unlockScroll();
        console.log('unlockScroll 호출 후');
        // HTML과 body의 overscroll-behavior 복원
        document.documentElement.style.overscrollBehavior = '';
        document.body.style.overscrollBehavior = '';
      }, 200);
    }
  }, [isOpen, selectedLanguage, menuItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // 언어 변경 시 메뉴 데이터 업데이트
  useEffect(() => {
    if (isOpen && menuItem) {

      // 언어가 변경되면 해당 언어의 데이터로 업데이트
      if (menuItem?.[selectedLanguage]) {
        setMenuName(menuItem[selectedLanguage].name || '');
        setMenuDescription(menuItem[selectedLanguage].description || '');
      } else {
        // 언어별 데이터가 없으면 기본값 사용
        setMenuName(menuItem?.name || menuItem?.ko?.name || '');
        setMenuDescription(menuItem?.description || menuItem?.ko?.description || '');
      }
    }
  }, [selectedLanguage, isOpen, menuItem]);

  // 페이지 뒤로가기 처리
  // onClose / showDeleteConfirm은 ref로 우회 → listener는 모달 열림 동안 1회만 등록
  // cleanup에서 history 좀비 정리 (UI 닫기 경로도 동일)
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const showDeleteConfirmRef = React.useRef(showDeleteConfirm);
  React.useEffect(() => { showDeleteConfirmRef.current = showDeleteConfirm; }, [showDeleteConfirm]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = (e) => {
      if (showDeleteConfirmRef.current) {
        setDeletePopupAnimating(false);
        setTimeout(() => setShowDeleteConfirm(false), 200);
      } else {
        onCloseRef.current?.();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.menuEdit) {
        window.history.back();
      }
    };
  }, [isOpen]);  // ← onClose, showDeleteConfirm 의존 제거
  
  // 삭제 팝업이 열릴 때 히스토리 추가
  useEffect(() => {
    if (showDeleteConfirm) {
      window.history.pushState({ modal: 'deleteMenu' }, '');
    }
  }, [showDeleteConfirm]);
  
  // 컴포넌트 unmount 시 스크롤 잠금 해제 보장
  useEffect(() => {
    return () => {
      // 컴포넌트가 unmount될 때 스크롤이 잠겨있다면 해제
      if (document.body.dataset.scrollY) {
        unlockScroll();
        document.documentElement.style.overscrollBehavior = '';
        document.body.style.overscrollBehavior = '';
      }
    };
  }, []);
  
  // 화면 크기에 따른 스케일 조정
  useEffect(() => {
    const scaleUI = () => {
      const wrapper = wrapperRef.current;
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };

    if (isVisible) {
      // 초기 스케일 설정만 수행
      scaleUI();
    }
  }, [isVisible]);
  
  // menuItem prop이 변경될 때 상태 업데이트
  useEffect(() => {
    console.log('menuItem useEffect 실행 - menuItem:', menuItem);

    const setupMenuItem = async () => {
      if (menuItem) {
        // 선택된 언어에 따라 해당 언어의 데이터 가져오기
        let name, description;

        if (selectedLanguage === 'ko') {
          name = menuItem.name_ko || menuItem.ko?.name || menuItem.menu_name || menuItem.name || '';
          description = menuItem.description_ko || menuItem.ko?.description || menuItem.menu_description || menuItem.description || '';
        } else if (selectedLanguage === 'en') {
          name = menuItem.name_en || menuItem.en?.name || '';
          description = menuItem.description_en || menuItem.en?.description || '';
        } else if (selectedLanguage === 'ja') {
          name = menuItem.name_ja || menuItem.ja?.name || '';
          description = menuItem.description_ja || menuItem.ja?.description || '';
        } else if (selectedLanguage === 'zh') {
          name = menuItem.name_zh || menuItem.zh?.name || '';
          description = menuItem.description_zh || menuItem.zh?.description || '';
        }

        // 고정세트는 basePrice 또는 price(숫자) 사용, 일반 메뉴는 해당 언어의 price 사용
        let price;
        if (menuItem.menuType === '고정세트' && (menuItem.basePrice || menuItem.price)) {
          // 고정세트는 숫자 가격 직접 사용
          price = menuItem.basePrice || menuItem.price || '';
        } else {
          // 일반 메뉴는 포맷된 가격 문자열 사용
          price = menuItem[selectedLanguage]?.price || menuItem.price || '';
        }

        const imageUrl = menuItem.image_url || menuItem.image || null;
        setMenuImage(imageUrl);
        setOriginalImage(imageUrl); // 원본 이미지 저장
        setNewImageFile(null); // 새 이미지 파일 초기화
        setMenuName(name);
        // 가격에서 숫자만 추출한 후 쉼표 포맷팅 적용
        const priceNumbers = typeof price === 'number' ? price.toString() : price.toString().replace(/[^0-9]/g, '');
        setMenuPrice(priceNumbers ? Number(priceNumbers).toLocaleString('ko-KR') : '');
        setMenuDescription(description);

        // 태그 (배열 또는 boolean 필드 모두 지원)
        setSelectedTags({
          popular: menuItem.is_popular || menuItem.tags?.includes('popular') || false,
          recommend: menuItem.is_recommend || menuItem.tags?.includes('recommend') || false,
          new: menuItem.is_new || menuItem.tags?.includes('new') || false,
          mostOrdered: menuItem.is_most_ordered || false
        });

        // 품절 상태
        const loadedSoldOut = menuItem.soldOut || menuItem.is_soldout || false;
        const loadedAutoResume = menuItem.auto_resume_soldout || false;
        setIsSoldOut(loadedSoldOut);
        setTogglePosition(loadedSoldOut ? 20 : 0);
        setAutoResumeNextDay(loadedAutoResume);
        initialSoldOutRef.current = { is_soldout: !!loadedSoldOut, auto_resume_soldout: !!loadedAutoResume };

        // 판매 시간 - DB 필드명과 기존 필드명 모두 확인
        // is_sales_time이 명시적으로 true이거나, 시간 값이 설정되어 있으면 활성화
        const hasSalesTime = menuItem.is_sales_time === true ||
                            (menuItem.start_time && menuItem.end_time) ||
                            (menuItem.openTime && menuItem.closeTime) ||
                            menuItem.salesTime;

        if (hasSalesTime) {
          setIsSalesTimeEnabled(true);
          setTimeTogglePosition(20);
          // 시간 포맷팅 함수 (초 제거)
          const formatTimeValue = (time) => {
            if (!time) return '';
            // HH:MM:SS 형식이면 초 제거
            if (typeof time === 'string' && time.match(/^\d{2}:\d{2}:\d{2}$/)) {
              return time.substring(0, 5);
            }
            return time;
          };
          // 새 DB 필드명 우선, 그 다음 기존 필드명 확인
          setStartTime(formatTimeValue(menuItem.start_time) || formatTimeValue(menuItem.openTime) || formatTimeValue(menuItem.salesTime?.start) || '');
          setEndTime(formatTimeValue(menuItem.end_time) || formatTimeValue(menuItem.closeTime) || formatTimeValue(menuItem.salesTime?.end) || '');
        } else {
          setIsSalesTimeEnabled(false);
          setTimeTogglePosition(0);
          setStartTime('');
          setEndTime('');
        }

        // dirty 비교용 — 로드 시점의 판매시간 스냅샷
        {
          const fmt = (t) => (typeof t === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(t)) ? t.substring(0, 5) : (t || '');
          initialSalesTimeRef.current = {
            is_sales_time: !!hasSalesTime,
            start_time: fmt(menuItem.start_time) || fmt(menuItem.openTime) || fmt(menuItem.salesTime?.start) || '',
            end_time: fmt(menuItem.end_time) || fmt(menuItem.closeTime) || fmt(menuItem.salesTime?.end) || '',
          };
        }

        // 옵션
        if (menuItem.options && Array.isArray(menuItem.options)) {
          let actualPricesData = {};

          // 선택 메뉴인 경우 실제 가격 조회 (await로 기다림)
          if (menuItem.menuType === '선택') {
            try {
              const itemCodes = [];
              menuItem.options.forEach(option => {
                const optionItems = option.choices || option.items || [];
                optionItems.forEach(choice => {
                  if (choice.itemCode) {
                    itemCodes.push(choice.itemCode);
                  }
                });
              });

              if (itemCodes.length > 0) {
                const response = await api.getMenuPrices(itemCodes);
                if (response.success) {
                  actualPricesData = response.prices;
                  // actualPricesData 변수만 사용, state 설정 제거
                }
              }
            } catch (error) {
              console.error('실제 가격 조회 실패:', error);
            }
          }

          const formattedOptions = menuItem.options.map((option, index) => {
            // 언어별 이름 선택
            // OKDC: option_group_name_ko/en/ja/zh
            // Union POS: group_name_ko, group_name, name
            let optionName = '';
            if (selectedLanguage === 'ko') {
              optionName = option.option_group_name_ko || option.group_name_ko || option.group_name || option.name || option.option_group_name || '';
            } else if (selectedLanguage === 'en') {
              optionName = option.option_group_name_en || option.group_name_en || option.name || option.option_group_name || '';
            } else if (selectedLanguage === 'ja') {
              optionName = option.option_group_name_ja || option.group_name_ja || option.name || option.option_group_name || '';
            } else if (selectedLanguage === 'zh') {
              optionName = option.option_group_name_zh || option.group_name_zh || option.name || option.option_group_name || '';
            }

            // OKDC는 items, Union POS는 choices
            const optionItems = option.choices || option.items || [];

            return {
              id: option.id || index + 1,  // 실제 DB ID 사용
              originalIndex: index,  // 원본 인덱스 저장 (매칭용)
              option_group_id: option.option_group_id,  // OKDC 매칭용
              option_type: option.option_type,  // OKDC: 'attribute' or 'side_dish'
              name: optionName,
              // 언어별 번역 원본 보존 — WowPos 한국어 저장 시 옵션이 delete+recreate 되므로,
              // payload 에 번역을 함께 재전송해야 _en/_ja/_zh 가 날아가지 않는다.
              _trGroup: {
                en: option.group_name_en ?? option.option_group_name_en ?? null,
                ja: option.group_name_ja ?? option.option_group_name_ja ?? null,
                zh: option.group_name_zh ?? option.option_group_name_zh ?? null,
              },
              isRequired: option.required || option.is_required || false,
              togglePosition: (option.required || option.is_required) ? 20 : 0,
              isExpanded: false,
              isNameFocused: false,
              choices: optionItems.map((choice, choiceIndex) => {
                // 언어별 선택지 이름
                // OKDC: option_name (다국어 지원 안함)
                // Union POS: option_name_ko, name
                let choiceName = '';
                if (selectedLanguage === 'ko') {
                  choiceName = choice.option_name_ko || choice.option_name || choice.name || '';
                } else if (selectedLanguage === 'en') {
                  choiceName = choice.option_name_en || choice.name || choice.option_name || '';
                } else if (selectedLanguage === 'ja') {
                  choiceName = choice.option_name_ja || choice.name || choice.option_name || '';
                } else if (selectedLanguage === 'zh') {
                  choiceName = choice.option_name_zh || choice.name || choice.option_name || '';
                }

                // 선택 메뉴이고 actualPrices가 있으면 실제 가격 사용
                let displayPrice = '';
                if (menuItem.menuType === '선택' && choice.itemCode && actualPricesData[choice.itemCode]) {
                  displayPrice = Number(actualPricesData[choice.itemCode]).toLocaleString();
                } else {
                  // OKDC: option_price, Union POS: price
                  const priceValue = choice.price || choice.option_price || 0;
                  displayPrice = priceValue ? Number(String(priceValue).replace(/[^0-9]/g, '')).toLocaleString() : '';
                }

                return {
                  id: choice.id || choiceIndex + 1,  // 실제 DB ID 사용
                  originalIndex: choiceIndex,  // 원본 인덱스 저장 (매칭용)
                  option_id: choice.option_id,  // OKDC 매칭용
                  sda_cd: choice.sda_cd,  // OKDC SDA용
                  sds_cd: choice.sds_cd,  // OKDC SDS용
                  name: choiceName,
                  // 선택지 언어별 번역 원본 보존 (그룹과 동일 이유)
                  _trChoice: {
                    en: choice.option_name_en ?? null,
                    ja: choice.option_name_ja ?? null,
                    zh: choice.option_name_zh ?? null,
                  },
                  price: displayPrice,
                  itemCode: choice.itemCode || null,
                  isNameFocused: false,
                  isPriceFocused: false
                };
              })
            };
          });
          setOptionsList(formattedOptions);
          setNextOptionId(formattedOptions.length + 1);
        } else {
          setOptionsList([]);
          setNextOptionId(1);
        }

        // 유효성 검증 에러 초기화
        setValidationErrors({});
      } else {
        // 새 메뉴 추가 모드 - 모든 필드 초기화
        setMenuImage(null);
        setOriginalImage(null);
        setNewImageFile(null);
        setMenuName('');
        setMenuPrice('');
        setMenuDescription('');
        setSelectedTags({ popular: false, recommend: false, new: false, mostOrdered: false });
        setIsSoldOut(false);
        setTogglePosition(0);
        setIsSalesTimeEnabled(false);
        setTimeTogglePosition(0);
        setStartTime('');
        setEndTime('');
        setOptionsList([]);
        setNextOptionId(1);
        setValidationErrors({});
      }
    };

    setupMenuItem();
  }, [menuItem, selectedLanguage]); // selectedLanguage 의존성 추가

  // ASP 실시간 품절 상태 조회 (OKDC 매장인 경우 페이지 열 때)
  useEffect(() => {
    console.log('[ASP 조회] useEffect 실행', {
      isOpen,
      menuItem: !!menuItem,
      pos_type: storeInfo.pos_type,
      storeInfo: storeInfo
    });
    if (isOpen && menuItem && storeInfo.pos_type === 'okdc') {
      console.log('[ASP 조회] checkASPStatus 호출 시작', { menuItemId: menuItem.id });
      checkASPStatus();
    } else {
      console.log('[ASP 조회] 조건 불충족:', {
        isOpen,
        hasMenuItem: !!menuItem,
        pos_type: storeInfo.pos_type,
        isOkdc: storeInfo.pos_type === 'okdc'
      });
    }
  }, [isOpen, menuItem, storeInfo.pos_type]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkASPStatus = async () => {
    try {
      setIsCheckingASP(true);

      // menuItem에서 실제 DB ID 찾기
      // menuItem.db_id 또는 menuItem.menu_id 또는 숫자형 id 사용
      const productId = menuItem.db_id || menuItem.menu_id || menuItem.id;

      console.log('[ASP 조회] menuItem 전체:', menuItem);
      console.log('[ASP 조회] 사용할 productId:', productId);

      const response = await api.getOKDCProductASPStatus(productId);

      if (response.success) {
        setAspSoldOutYn(response.aspSoldOutYn);
        console.log('[ASP 조회] 품절 상태:', response.aspSoldOutYn);
      }
    } catch (err) {
      console.error('[ASP 조회] 실패:', err);
      // 실패해도 계속 진행 (웹 품절 관리는 가능)
      setAspSoldOutYn(null);
    } finally {
      setIsCheckingASP(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`menu-edit-overlay ${isAnimating ? 'open' : ''}`}
      onClick={(e) => {
        console.log('overlay clicked', e.target.className);
        // 바텀시트 내부를 클릭한 경우가 아닌지 확인
        if (e.target.className && e.target.className.includes('menu-edit-overlay')) {
          onClose();
        }
      }}
    >
      <div 
        ref={bottomSheetRef}
        className={`menu-edit-sheet ${isAnimating ? 'open' : ''} ${isDragging ? 'dragging' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          ref={wrapperRef}
          className="menu-edit-wrapper"
          style={{ 
            transform: `translateY(${currentY}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: isDragging && currentY > 50 ? 0.95 : 1
          }}
        >
        {/* 드래그 핸들 영역 */}
        <div 
          className="drag-handle-area"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="drag-handle" />
        </div>

        {/* 헤더 섹션 */}
        <div className="header-section">
          <h2 className="header-title">{isWowposAdd ? '메뉴 추가' : t.headerTitle}</h2>
        </div>

        {/* 콘텐츠 영역 */}
        <div className="content" ref={contentRef}>
          {/* 메뉴 수정 폼 */}
          <div className="form-container">
            {/* 메뉴 기본 정보 그룹 */}
            <div className="menu-basic-info-group">
              {/* 이미지 업로드 필드 */}
              <div className="image-upload-container">
                <div 
                  className={`image-upload-field ${menuImage ? 'has-image' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {menuImage ? (
                    <img src={menuImage} alt="menu" className="uploaded-image" />
                  ) : (
                    <div className="upload-placeholder">
                      <img 
                        src={adminEmptyImgIcon}
                        alt="upload"
                        width="38"
                        height="38"
                      />
                      <span>{t.imageUpload}</span>
                    </div>
                  )}
                </div>
                {menuImage && (
                  <button
                    className="image-delete-btn"
                    onClick={async (e) => {
                      e.stopPropagation();

                      // UI에서만 이미지 제거 (실제 삭제는 저장 버튼 클릭 시)
                      setTimeout(() => {
                        setMenuImage(null);
                        setNewImageFile(null); // 새 이미지 파일도 제거
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }, 150);
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.transform = 'scale(0.9)';
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.transform = 'scale(0.9)';
                    }}
                    onTouchEnd={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <img 
                      src={adminDeleteIcon}
                      alt="delete"
                      className="delete-icon"
                    />
                  </button>
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;

                  // 업로드 직전이 아니라 여기서 압축한다 — 미리보기와 실제 올라가는
                  // 바이트가 같아야 사장님이 본 그대로가 손님에게 간다.
                  // 압축 중 저장을 누르면 이미지가 조용히 누락되므로 기존 isProcessing
                  // 플래그를 재사용해 저장 버튼을 잠근다.
                  setIsProcessing(true);
                  const compressed = await compressImageFile(file);
                  setIsProcessing(false);

                  // 파일을 저장하고 미리보기 표시
                  setNewImageFile(compressed);

                  // 미리보기를 위해 FileReader 사용
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setMenuImage(reader.result);
                    console.log('이미지 미리보기 설정');
                  };
                  reader.readAsDataURL(compressed);
                }}
              />
              
              {/* 메뉴명 및 가격 입력 필드 */}
              <div className="input-fields">
                <input
                  key={`name-${animationKey}`}
                  type="text"
                  className={`menu-name-input ${menuName ? 'has-value' : ''} ${isNameFocused ? 'focused' : ''} ${validationErrors['menu-name'] ? 'error' : ''} ${(selectedLanguage === 'ko' && !wpEditable) ? 'readonly' : ''}`}
                  placeholder={t.menuNamePlaceholder}
                  value={menuName}
                  readOnly={selectedLanguage === 'ko' && !wpEditable}
                  disabled={selectedLanguage === 'ko' && !wpEditable}
                  onChange={(e) => {
                    // 와우포스 매장(추가/수정) 은 한국어도 입력 가능
                    if (selectedLanguage === 'ko' && !wpEditable) {
                      e.preventDefault();
                      return;
                    }
                    setMenuName(e.target.value);
                    if (validationErrors['menu-name']) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors['menu-name'];
                        return newErrors;
                      });
                    }
                  }}
                  onFocus={(e) => {
                    if (selectedLanguage === 'ko' && !wpEditable) {
                      e.target.blur();
                      return;
                    }
                    setIsNameFocused(true);
                  }}
                  onBlur={() => setIsNameFocused(false)}
                />

                <div className="menu-price-wrapper">
                  <input
                    key={`price-${animationKey}`}
                    type="text"
                    inputMode="numeric"
                    className={`menu-price-input ${menuPrice ? 'has-value' : ''} ${isPriceFocused ? 'focused' : ''} ${validationErrors['menu-price'] ? 'error' : ''} ${!wpEditable ? 'readonly' : ''}`}
                    placeholder={t.menuPricePlaceholder}
                    value={menuPrice}
                    readOnly={!wpEditable}
                    disabled={!wpEditable}
                    onChange={(e) => {
                      // 와우포스 매장(추가/수정) 은 가격 입력 가능 (숫자만)
                      if (!wpEditable) {
                        e.preventDefault();
                        return;
                      }
                      const numeric = e.target.value.replace(/[^0-9]/g, '');
                      setMenuPrice(numeric);
                    }}
                    onFocus={() => setIsPriceFocused(true)}
                    onBlur={() => setIsPriceFocused(false)}
                  />
                  <span className="currency">{t.currency}</span>
                </div>
              </div>
            </div>
            
            {/* 메뉴 설명 입력 필드 */}
            <textarea
              className={`menu-description-input ${menuDescription ? 'has-value' : ''} ${isDescriptionFocused ? 'focused' : ''}`}
              placeholder="메뉴를 설명해주세요"
              value={menuDescription}
              onChange={(e) => setMenuDescription(e.target.value)}
              onFocus={() => setIsDescriptionFocused(true)}
              onBlur={() => setIsDescriptionFocused(false)}
            />
          </div>
          
          {/* 태그 선택 박스 */}
          <div className="tag-box">
            <span className="tag-label">태그</span>
            <div className="tag-buttons">
              <button 
                className={`tag-button ${selectedTags.popular ? 'active' : ''}`}
                onClick={() => handleTagToggle('popular')}
              >
                인기
              </button>
              <button 
                className={`tag-button ${selectedTags.recommend ? 'active' : ''}`}
                onClick={() => handleTagToggle('recommend')}
              >
                추천
              </button>
              <button
                className={`tag-button ${selectedTags.new ? 'active' : ''}`}
                onClick={() => handleTagToggle('new')}
              >
                신메뉴
              </button>
              <button
                className={`tag-button ${selectedTags.mostOrdered ? 'active' : ''}`}
                onClick={() => handleTagToggle('mostOrdered')}
              >
                많이 주문
              </button>
            </div>
          </div>
          
          {/* 품절 상태 토글 박스 (품절 On 시 '다음 영업일 자동 재판매' 체크박스 펼침) */}
          <div className={`soldout-box ${isSoldOut ? 'expanded' : ''}`}>
            <div className="soldout-header" onClick={handleToggleClick}>
              <span className="soldout-label">품절</span>
              <div
                className={`toggle-switch ${isSoldOut ? 'on' : 'off'}`}
              >
                <div
                  className="toggle-thumb"
                  style={{
                    transform: `translateX(${togglePosition}px)`,
                    transition: isDraggingToggle ? 'none' : 'transform 0.2s ease'
                  }}
                  onMouseDown={handleToggleStart}
                  onTouchStart={handleToggleStart}
                />
              </div>
            </div>

            <div className={`soldout-detail-wrapper ${isSoldOut ? 'expanded' : 'collapsed'}`}>
              <div
                className="soldout-resume-row"
                onClick={(e) => {
                  e.stopPropagation();
                  setResumeCheckAnimating(true);
                  setTimeout(() => setResumeCheckAnimating(false), 250);
                  setAutoResumeNextDay(v => !v);
                }}
              >
                <span className="soldout-resume-label">다음 영업일에 자동으로 다시 판매</span>
                <div className="option-checkbox">
                  <div className={`checkbox-wrapper ${autoResumeNextDay ? 'selected' : ''} ${resumeCheckAnimating ? 'animating' : ''}`}>
                    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <mask id="soldout-resume-mask">
                          <rect x="-19" y="0" width="19" height="19" fill="white" className="checkbox-mask-rect"/>
                        </mask>
                      </defs>
                      <rect x="0" y="0" width="19" height="19" rx="4" fill="#FDCD27"/>
                      <rect x="0" y="0" width="19" height="19" rx="4" className="checkbox-white" fill="white"/>
                      <path className="checkbox-mark" d="M14.7583 5.27726C14.9131 5.45463 15 5.69518 15 5.94599C15 6.19681 14.9131 6.43735 14.7583 6.61473L8.5713 13.7044C8.48954 13.7981 8.39247 13.8725 8.28563 13.9232C8.17879 13.9739 8.06428 14 7.94864 14C7.83299 14 7.71848 13.9739 7.61165 13.9232C7.50481 13.8725 7.40774 13.7981 7.32597 13.7044L4.252 10.1826C4.17316 10.0953 4.11028 9.99096 4.06702 9.87556C4.02375 9.76016 4.00098 9.63604 4.00003 9.51044C3.99908 9.38485 4.01996 9.2603 4.06147 9.14405C4.10297 9.02781 4.16427 8.9222 4.24177 8.83339C4.31927 8.74458 4.41144 8.67434 4.51288 8.62678C4.61433 8.57922 4.72302 8.55529 4.83262 8.55638C4.94223 8.55747 5.05054 8.58357 5.15125 8.63314C5.25196 8.68271 5.34304 8.75477 5.41919 8.84511L7.94836 11.7433L13.5906 5.27726C13.6672 5.18936 13.7583 5.11963 13.8584 5.07206C13.9586 5.02449 14.066 5 14.1745 5C14.2829 5 14.3903 5.02449 14.4905 5.07206C14.5906 5.11963 14.6817 5.18936 14.7583 5.27726Z" fill="white" fillRule="evenodd" clipRule="evenodd" mask="url(#soldout-resume-mask)"/>
                      <rect x="1" y="1" width="17" height="17" rx="3" className="checkbox-border" stroke="#DDDDDD" strokeWidth={2} fill="none"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 판매시간 설정 박스 */}
          <div className={`sales-time-box ${isSalesTimeEnabled ? 'expanded' : ''}`}>
            <div className="sales-time-header" onClick={handleTimeToggleClick}>
              <span className="sales-time-label">판매시간</span>
              <div 
                className={`toggle-switch ${isSalesTimeEnabled ? 'on' : 'off'}`}
              >
                <div 
                  className="toggle-thumb"
                  style={{ 
                    transform: `translateX(${timeTogglePosition}px)`,
                    transition: isDraggingTimeToggle ? 'none' : 'transform 0.2s ease'
                  }}
                  onMouseDown={handleTimeToggleStart}
                  onTouchStart={handleTimeToggleStart}
                />
              </div>
            </div>
            
            <div className={`time-input-wrapper ${isSalesTimeEnabled ? 'expanded' : 'collapsed'}`}>
              <div className="time-input-container">
                <input
                  ref={startTimeRef}
                  key={`start-${timeAnimationKey}`}
                  type="text"
                  inputMode="numeric"
                  className={`time-input ${startTime ? 'has-value' : ''} ${isStartTimeFocused ? 'focused' : ''} ${startTimeError ? 'error' : ''}`}
                  placeholder="00:00"
                  value={startTime}
                  onChange={handleStartTimeChange}
                  onFocus={() => setIsStartTimeFocused(true)}
                  onBlur={() => setIsStartTimeFocused(false)}
                  maxLength="5"
                />
                <span className="time-separator">~</span>
                <input
                  ref={endTimeRef}
                  key={`end-${timeAnimationKey}`}
                  type="text"
                  inputMode="numeric"
                  className={`time-input ${endTime ? 'has-value' : ''} ${isEndTimeFocused ? 'focused' : ''} ${endTimeError ? 'error' : ''}`}
                  placeholder="23:59"
                  value={endTime}
                  onChange={handleEndTimeChange}
                  onFocus={() => setIsEndTimeFocused(true)}
                  onBlur={() => setIsEndTimeFocused(false)}
                  maxLength="5"
                />
              </div>
            </div>
          </div>

          {/* 옵션 박스 */}
          {(optionsList.length > 0 || wpEditable) && (
          <div className="option-box">
            <div className={`option-header ${optionsList.length > 0 ? 'expanded' : ''}`}>
              <span className="option-box-label">옵션</span>
              {wpEditable && (
                <button type="button" className="wp-opt-add-group" onClick={handleAddOptionGroup}>+</button>
              )}
            </div>
            <div className="option-items-container">
            {optionsList.map((option) => (
              <div
                key={option.id}
                data-option-id={option.id}
                className={`option-detail-box ${!option.isExpanded ? 'collapsed' : ''}`}
                onClick={!option.isExpanded ? () => {
                  // 축소 상태에서 바 영역 클릭 시 확장
                  const newList = [...optionsList];
                  const index = newList.findIndex(opt => opt.id === option.id);
                  newList[index].isExpanded = true;
                  setOptionsList(newList);
                } : undefined}
                style={{
                  cursor: !option.isExpanded ? 'pointer' : 'default',
                  ...(option._pendingDelete ? { opacity: 0.45 } : {}),
                }}
              >
                {/* 확장/축소 아이콘 */}
                <img
                  src={adminDropdownIcon}
                  alt="expand"
                  className={`expand-icon ${option.isExpanded ? 'expanded' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation(); // 부모 클릭 이벤트 방지
                    // 단순 토글 (유효성 검사 없이)
                    const newList = [...optionsList];
                    const index = newList.findIndex(opt => opt.id === option.id);
                    newList[index].isExpanded = !newList[index].isExpanded;
                    setOptionsList(newList);
                  }}
                />
                
                {/* 축소 상태일 때 옵션 이름 표시 */}
                {!option.isExpanded && option.name && (
                  <div className="collapsed-option-name">{option.name}</div>
                )}
                
                {/* 확장 상태일 때만 내용 표시 */}
                {option.isExpanded && (
                  <>
                {/* 옵션 이름 입력 */}
                <div className="option-name-section">
                  <span className="option-label">옵션 이름</span>
                  <input
                    key={`option-${option.id}-${animationKey}`}
                    type="text"
                    className={`option-name-input ${option.name && !option.isNameFocused ? 'has-value' : ''} ${option.isNameFocused ? 'focused' : ''} ${validationErrors[`option-${option.id}-name`] ? 'error' : ''} ${(selectedLanguage === 'ko' && !wpEditable) ? 'readonly' : ''}`}
                    placeholder="Ex) 음료, 사이즈, 맛 선택"
                    value={option.name}
                    readOnly={selectedLanguage === 'ko' && !wpEditable}
                    disabled={selectedLanguage === 'ko' && !wpEditable}
                    onChange={(e) => {
                      // 한국어일 때는 변경 불가 (단, WowPos 는 원본도 직접 편집 가능)
                      if (selectedLanguage === 'ko' && !wpEditable) {
                        e.preventDefault();
                        return;
                      }
                      const newList = [...optionsList];
                      const index = newList.findIndex(opt => opt.id === option.id);
                      newList[index].name = e.target.value;
                      setOptionsList(newList);
                      // 입력 시 에러 상태 제거
                      const errorKey = `option-${option.id}-name`;
                      if (validationErrors[errorKey]) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors[errorKey];
                          return newErrors;
                        });
                      }
                    }}
                    onFocus={() => {
                      const newList = [...optionsList];
                      const index = newList.findIndex(opt => opt.id === option.id);
                      newList[index].isNameFocused = true;
                      setOptionsList(newList);
                    }}
                    onBlur={() => {
                      const newList = [...optionsList];
                      const index = newList.findIndex(opt => opt.id === option.id);
                      newList[index].isNameFocused = false;
                      setOptionsList(newList);
                    }}
                  />
                </div>
                
                {/* 필수 옵션 토글 */}
                <div className="required-option-section">
                  <span className="required-option-label">옵션 필수 여부</span>
                  <div
                    className={`toggle-switch ${option.isRequired ? 'on' : 'off'} ${(storeInfo?.pos_type === 'OKDC' || wpEditable) ? '' : 'disabled'}`}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      // WowPos: 로컬 토글 (ON=하나만 선택/필수, OFF=여러 개 선택)
                      if (wpEditable) {
                        setOptionsList(prev => prev.map(opt =>
                          opt.id === option.id
                            ? { ...opt, isRequired: !opt.isRequired, togglePosition: !opt.isRequired ? 20 : 0 }
                            : opt
                        ));
                        return;
                      }

                      // OKDC 매장만 토글 가능
                      if (storeInfo?.pos_type !== 'OKDC') {
                        console.log('필수 옵션 설정은 OKDC 매장에서만 변경 가능합니다.');
                        return;
                      }

                      const newIsRequired = !option.isRequired;

                      try {
                        // 옵션 타입 결정 (option_type 또는 이름으로 판단)
                        const optionType = menuItem.options?.find(opt =>
                          (opt.option_group_name || opt.name) === option.name
                        )?.option_type || 'attribute';

                        // API 호출
                        const response = await api.put(
                          `/api/menus/okdc/option-group/${storeInfo.id}/${optionType}/${option.id}/required`,
                          { isRequired: newIsRequired }
                        );

                        if (response.data.success) {
                          // 로컬 상태 업데이트
                          setOptionsList(prev => prev.map(opt =>
                            opt.id === option.id
                              ? { ...opt, isRequired: newIsRequired, togglePosition: newIsRequired ? 20 : 0 }
                              : opt
                          ));
                          console.log(`옵션 "${option.name}" 필수여부: ${newIsRequired ? '필수' : '선택'}`);
                        }
                      } catch (error) {
                        console.error('필수여부 업데이트 실패:', error);
                        alert('옵션 필수여부 업데이트에 실패했습니다.');
                      }
                    }}
                    style={{
                      cursor: (storeInfo?.pos_type === 'OKDC' || wpEditable) ? 'pointer' : 'not-allowed',
                      opacity: (storeInfo?.pos_type === 'OKDC' || wpEditable) ? 1 : 0.6
                    }}
                  >
                    <div
                      className="toggle-thumb"
                      style={{
                        transform: `translateX(${option.togglePosition}px)`,
                        transition: 'transform 0.2s ease'
                      }}
                    />
                  </div>
                </div>
                
                {/* 선택지 섹션 */}
                <div className="choices-section">
                  <span className="choices-label">선택지</span>
                  <div className="choices-list">
                    {option.choices.map((choice, choiceIndex) => (
                      <div 
                        key={choice.id} 
                        className={`choice-item ${deletingChoices[`${option.id}-${choice.id}`] ? 'deleting' : ''}`}
                        style={{
                          transition: deletingChoices[`${option.id}-${choice.id}`] ? 'opacity 0.15s ease-out, transform 0.15s ease-out, height 0.2s ease-in-out 0.1s, margin 0.2s ease-in-out 0.1s' : 'none'
                        }}>
                        <input
                          key={`choice-${option.id}-${choiceIndex}-${animationKey}`}
                          type="text"
                          enterKeyHint="next"
                          className={`choice-name-input ${choice.name ? 'has-value' : ''} ${choiceFocusStates[`${option.id}-${choice.id}`] ? 'focused' : ''} ${validationErrors[`option-${option.id}-choice-${choiceIndex}-name`] ? 'error' : ''} ${(selectedLanguage === 'ko' && !wpEditable) ? 'readonly' : ''}`}
                          placeholder="옵션명"
                          value={choice.name}
                          readOnly={selectedLanguage === 'ko' && !wpEditable}
                          disabled={selectedLanguage === 'ko' && !wpEditable}
                          onChange={(e) => {
                            // 한국어일 때는 변경 불가 (단, WowPos 는 원본도 직접 편집 가능)
                            if (selectedLanguage === 'ko' && !wpEditable) {
                              e.preventDefault();
                              return;
                            }
                            const newList = [...optionsList];
                            const optIndex = newList.findIndex(opt => opt.id === option.id);
                            newList[optIndex].choices[choiceIndex].name = e.target.value;
                            setOptionsList(newList);
                            // 입력 시 에러 상태 제거
                            const errorKey = `option-${option.id}-choice-${choiceIndex}-name`;
                            if (validationErrors[errorKey]) {
                              setValidationErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[errorKey];
                                return newErrors;
                              });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              // 같은 줄의 가격 입력 필드로 포커스 이동
                              const priceInput = e.target.parentElement.querySelector('.choice-price-input');
                              if (priceInput) {
                                priceInput.focus();
                              }
                            }
                          }}
                          onFocus={() => {
                            setChoiceFocusStates(prev => ({...prev, [`${option.id}-${choice.id}`]: true}));
                          }}
                          onBlur={() => {
                            setChoiceFocusStates(prev => ({...prev, [`${option.id}-${choice.id}`]: false}));
                          }}
                        />
                        <div 
                          key={`price-wrapper-${option.id}-${choiceIndex}-${animationKey}`}
                          className={`choice-price-wrapper ${choice.price ? 'has-value' : ''} ${validationErrors[`option-${option.id}-choice-${choiceIndex}-price`] ? 'error' : ''} ${choicePriceFocusStates[`${option.id}-${choice.id}`] ? 'focused' : ''}`}>
                          <span className="price-plus">+</span>
                          <input
                            key={`choice-price-${option.id}-${choiceIndex}-${animationKey}`}
                            type="text"
                            inputMode="numeric"
                            enterKeyHint={choiceIndex < option.choices.length - 1 ? "next" : "done"}
                            className={`choice-price-input ${choice.price ? 'has-value' : ''} ${!wpEditable ? 'readonly' : ''}`}
                            placeholder="0"
                            value={choice.price}
                            readOnly={!wpEditable}
                            disabled={!wpEditable}
                            onChange={(e) => {
                              // WowPos 는 추가금액 직접 입력, 그 외(POS)는 읽기 전용
                              if (!wpEditable) { e.preventDefault(); return; }
                              const digits = e.target.value.replace(/[^0-9]/g, '');
                              const newList = [...optionsList];
                              const optIndex = newList.findIndex(opt => opt.id === option.id);
                              newList[optIndex].choices[choiceIndex].price = digits ? Number(digits).toLocaleString() : '';
                              setOptionsList(newList);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === 'Go' || e.keyCode === 13) {
                                e.preventDefault();
                                // 다음 선택지의 이름 입력 필드로 포커스 이동
                                const nextChoiceIndex = choiceIndex + 1;
                                if (nextChoiceIndex < option.choices.length) {
                                  // setTimeout을 사용하여 DOM 업데이트 후 포커스 이동
                                  setTimeout(() => {
                                    const nextInput = document.querySelector(
                                      `.option-detail-box[data-option-id="${option.id}"] .choice-item:nth-child(${nextChoiceIndex + 1}) .choice-name-input`
                                    );
                                    if (nextInput) {
                                      nextInput.focus();
                                    }
                                  }, 0);
                                } else {
                                  // 마지막 선택지인 경우 포커스 해제
                                  e.target.blur();
                                }
                              }
                            }}
                            onFocus={() => {
                              setChoicePriceFocusStates(prev => ({...prev, [`${option.id}-${choice.id}`]: true}));
                            }}
                            onBlur={() => {
                              setChoicePriceFocusStates(prev => ({...prev, [`${option.id}-${choice.id}`]: false}));
                            }}
                          />
                          <span className="price-won">원</span>
                        </div>
                        {wpEditable && (
                          <button type="button" className="wp-choice-del" onClick={() => handleDeleteChoice(option.id, choice.id)} aria-label="선택지 삭제">
                            <img src={adminDeleteIcon} alt="선택지 삭제" className="wp-choice-del-icon" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {wpEditable && (
                    <button type="button" className="wp-choice-add" onClick={() => handleAddChoice(option.id)}>+ 추가</button>
                  )}
                </div>
                  {wpEditable && (
                    <button type="button" className="wp-group-del" onClick={() => handleDeleteOptionGroup(option.id)}>
                      {option._pendingDelete ? '삭제 취소' : '옵션 그룹 삭제'}
                    </button>
                  )}
                  </>
                )}
              </div>
            ))}
            </div>
          </div>
          )}

          {/* 메뉴 삭제 버튼 — WowPos 매장 + 수정 모드 (신규 추가 X). 옵션 박스 아래 위치 */}
          {isWowposStore && !isWowposAdd && menuItem?.id && (
            <button
              type="button"
              className="menu-delete-btn"
              onClick={async () => {
                if (!window.confirm('이 메뉴를 삭제할까요? 이미지도 함께 삭제됩니다.')) return;
                try {
                  const realId = menuItem.db_id || (typeof menuItem.id === 'string' ? menuItem.id.replace(/^menu-/, '') : menuItem.id);
                  const r = await fetch(`/api/wowpos/menus/${realId}`, { method: 'DELETE', headers: authHeaders() });
                  const d = await r.json();
                  if (!d.success) throw new Error(d.error || '삭제 실패');
                  onShowToast && onShowToast('메뉴가 삭제되었습니다', 'success');
                  if (typeof onDeleteMenu === 'function') {
                    try { onDeleteMenu(category, menuItem.id, false); } catch (_) {}
                  }
                  onClose && onClose();
                } catch (err) {
                  console.error('[메뉴 삭제] 오류:', err);
                  onShowToast && onShowToast(err.message || '메뉴 삭제 실패', 'error');
                }
              }}
            >
              메뉴 삭제
            </button>
          )}

          {/* 스크롤 여백 */}
          <div style={{ height: '100px' }} />
        </div>

        {/* 하단 버튼 */}
        <div 
          className="bottom-actions"
          style={{
            display: isKeyboardVisible ? 'none' : 'flex'
          }}>
          <button className="action-button cancel-button" onClick={() => {
            // 취소 버튼 - 수정 내용 저장하지 않고 닫기
            onClose();
          }}>
            취소
          </button>
          <button className="action-button add-button" onClick={async () => {
            // 와우포스 신규 추가는 별도 흐름 — 이름만 필수, 가격 0 디폴트.
            if (isWowposAdd) {
              await handleWowposAddSave();
              return;
            }
            // 입력 검증
            const errors = {};
            let hasErrors = false;

            // 메뉴명과 가격 검증
            if (!menuName) {
              errors['menu-name'] = true;
              hasErrors = true;
            }
            // 와우포스 매장은 가격 0 허용 — 빈 값만 fail. 그 외는 기존대로 빈 값 fail.
            if (!menuPrice && !wpEditable) {
              errors['menu-price'] = true;
              hasErrors = true;
            }
            
            // 판매시간 검증 (토글이 ON일 때만)
            if (isSalesTimeEnabled) {
              if (!startTime || startTime.length < 5) {
                setStartTimeError(true);
                hasErrors = true;
              } else if (!validateTime(startTime)) {
                setStartTimeError(true);
                hasErrors = true;
              }
              
              if (!endTime || endTime.length < 5) {
                setEndTimeError(true);
                hasErrors = true;
              } else if (!validateTime(endTime)) {
                setEndTimeError(true);
                hasErrors = true;
              }
              
              // 에러가 있으면 애니메이션 키 업데이트
              if (hasErrors) {
                setTimeAnimationKey(prev => prev + 1);
              }
            }
            
            // 옵션 검증 및 문제가 있는 옵션 펼치기
            const updatedOptionsList = [...optionsList];
            let hasOptionErrors = false;
            
            optionsList.forEach((option, optionIndex) => {
              let hasThisOptionError = false;
              
              if (!option.name) {
                errors[`option-${option.id}-name`] = true;
                hasErrors = true;
                hasThisOptionError = true;
              }
              
              option.choices.forEach((choice, idx) => {
                if (!choice.name) {
                  errors[`option-${option.id}-choice-${idx}-name`] = true;
                  hasErrors = true;
                  hasThisOptionError = true;
                }
                // 가격 필드는 readOnly이므로 유효성 검사 제외
                // POS에서 0원 옵션도 허용됨
              });
              
              // 이 옵션에 에러가 있으면 자동으로 펼치기
              if (hasThisOptionError && !option.isExpanded) {
                updatedOptionsList[optionIndex].isExpanded = true;
                hasOptionErrors = true;
              }
            });
            
            // 문제가 있는 옵션들을 펼친 상태로 업데이트
            if (hasOptionErrors) {
              setOptionsList(updatedOptionsList);
            }
            
            if (hasErrors) {
              setValidationErrors(errors);
              // 애니메이션 재시작을 위해 키 변경
              setAnimationKey(prev => prev + 1);
              return;
            }

            // 판매시간 dirty 스냅샷 — 아래 onUpdate() 가 menuItem 을 즉시 갱신하면
            // menuItem 로드 useEffect 가 initialSalesTimeRef 를 "변경 후 값" 으로 덮어써
            // 저장 끝에서 dirty 가 항상 false 가 된다. 그 전에 여기서 미리 고정한다.
            const _salesInitSnapshot = initialSalesTimeRef.current;
            const _salesCurSnapshot = {
              is_sales_time: !!isSalesTimeEnabled,
              start_time: isSalesTimeEnabled ? startTime : '',
              end_time: isSalesTimeEnabled ? endTime : '',
            };
            const _salesTimeDirtySnapshot = _salesInitSnapshot.is_sales_time !== _salesCurSnapshot.is_sales_time
              || _salesInitSnapshot.start_time !== _salesCurSnapshot.start_time
              || _salesInitSnapshot.end_time !== _salesCurSnapshot.end_time;

            // 품절/자동해제 dirty 스냅샷 (판매시간과 동일 이유 — onUpdate 전에 고정)
            const _soldOutInitSnapshot = initialSoldOutRef.current;
            const _soldOutCurSnapshot = {
              is_soldout: !!isSoldOut,
              auto_resume_soldout: isSoldOut ? !!autoResumeNextDay : false,
            };
            const _soldOutDirtySnapshot = _soldOutInitSnapshot.is_soldout !== _soldOutCurSnapshot.is_soldout
              || _soldOutInitSnapshot.auto_resume_soldout !== _soldOutCurSnapshot.auto_resume_soldout;

            // 메뉴 데이터 준비
            // 가격에서 쉼표 제거하고 포맷팅
            const priceNumber = menuPrice.replace(/,/g, '');
            const formattedPrice = Number(priceNumber).toLocaleString('ko-KR');
            
            const newMenuItem = {
              id: menuItem?.id || `temp-${Date.now()}`, // 수정 모드일 때는 기존 ID 유지
              ko: {
                name: menuName,
                description: menuDescription || '',
                price: `${formattedPrice}원`
              },
              en: {
                name: menuName,
                description: menuDescription || '',
                price: `₩${formattedPrice}`
              },
              ja: {
                name: menuName,
                description: menuDescription || '',
                price: `₩${formattedPrice}`
              },
              zh: {
                name: menuName,
                description: menuDescription || '',
                price: `₩${formattedPrice}`
              },
              image: menuImage || null,
              tags: Object.keys(selectedTags).filter(key => selectedTags[key]),
              soldOut: isSoldOut,
              openTime: isSalesTimeEnabled ? startTime : null,
              closeTime: isSalesTimeEnabled ? endTime : null,
              options: optionsList.map(option => ({
                name: option.name,
                required: option.isRequired,
                choices: option.choices.map(choice => ({
                  name: choice.name,
                  price: choice.price ? choice.price.toString().replace(/[^0-9]/g, '') : '0',
                  itemCode: choice.itemCode || null // itemCode 유지 (Union 옵션인 경우)
                }))
              })),
              hasOptions: optionsList.length > 0,
              // 가격 정보 추가 (고정세트의 경우 basePrice 사용)
              price: parseInt(priceNumber),
              basePrice: parseInt(priceNumber),
              // 기존 메뉴의 중요한 정보들 유지
              itemCode: menuItem?.itemCode || null, // ItemCode 유지 (Union 메뉴인 경우)
              userModified: menuItem?.userModified || null, // 기존 수정 플래그 유지
              menuType: menuItem?.menuType || null // 메뉴 타입 유지
            };
            
            // 메뉴 추가 함수 호출
            console.log('=== 메뉴 추가 시작 ===');
            console.log('category:', category);
            console.log('newMenuItem:', newMenuItem);
            console.log('onAddMenu 존재 여부:', !!onAddMenu);

            // 처리 중임을 표시
            console.log('메뉴 업데이트 처리 시작');
            setIsProcessing(true);

            // 즉시 UI 업데이트와 백엔드 처리를 동시에 수행
            const processUpdate = async () => {
              try {
                // UI를 먼저 업데이트 (즉시 반영)
                if (menuItem && menuItem.id && onUpdate) {
              // 이미지 URL 결정 - 명확한 조건 처리
              let imageUrl;
              const isImageDeleted = originalImage && !menuImage && !newImageFile;

              if (isImageDeleted) {
                // 이미지가 삭제된 경우 (원래 있었는데 현재 없음)
                imageUrl = null;
                console.log('이미지 삭제 감지 - UI 업데이트');
              } else if (newImageFile) {
                // 새 이미지가 업로드된 경우
                imageUrl = URL.createObjectURL(newImageFile);
                console.log('새 이미지 업로드 - blob URL 생성');
              } else if (menuImage) {
                // 기존 이미지를 유지하는 경우
                imageUrl = menuImage;
                console.log('기존 이미지 유지');
              } else {
                // 원래부터 이미지가 없었던 경우
                imageUrl = null;
                console.log('원래 이미지 없음');
              }

              // 완전히 새로운 객체 생성 - 기존 참조 제거
              const immediateUpdate = {
                id: menuItem.id,
                itemCode: menuItem.itemCode,
                menu_code: menuItem.menu_code,
                menuType: menuItem.menuType,
                image_url: imageUrl,
                image: imageUrl,
                price: parseInt(priceNumber),
                is_soldout: isSoldOut,
                auto_resume_soldout: isSoldOut ? autoResumeNextDay : false,
                menu_name: selectedLanguage === 'ko' ? menuName : (menuItem.menu_name || menuItem.name_ko),
                menu_description: selectedLanguage === 'ko' ? menuDescription : (menuItem.menu_description || menuItem.description_ko),
                // 현재 선택된 언어에 대한 필드
                name: menuName,
                description: menuDescription,
                // 언어별 객체 구조 - 기존 값 유지하면서 현재 언어만 업데이트
                ko: {
                  name: selectedLanguage === 'ko' ? menuName : (menuItem.ko?.name || menuItem.name_ko || menuItem.menu_name),
                  description: selectedLanguage === 'ko' ? menuDescription : (menuItem.ko?.description || menuItem.description_ko || ''),
                  price: selectedLanguage === 'ko' ? `${parseInt(priceNumber).toLocaleString()}원` : menuItem.ko?.price
                },
                en: {
                  name: selectedLanguage === 'en' ? menuName : (menuItem.en?.name || menuItem.name_en || ''),
                  description: selectedLanguage === 'en' ? menuDescription : (menuItem.en?.description || menuItem.description_en || ''),
                  price: selectedLanguage === 'en' ? `₩${parseInt(priceNumber).toLocaleString()}` : menuItem.en?.price
                },
                ja: {
                  name: selectedLanguage === 'ja' ? menuName : (menuItem.ja?.name || menuItem.name_ja || ''),
                  description: selectedLanguage === 'ja' ? menuDescription : (menuItem.ja?.description || menuItem.description_ja || ''),
                  price: selectedLanguage === 'ja' ? `₩${parseInt(priceNumber).toLocaleString()}` : menuItem.ja?.price
                },
                zh: {
                  name: selectedLanguage === 'zh' ? menuName : (menuItem.zh?.name || menuItem.name_zh || ''),
                  description: selectedLanguage === 'zh' ? menuDescription : (menuItem.zh?.description || menuItem.description_zh || ''),
                  price: selectedLanguage === 'zh' ? `₩${parseInt(priceNumber).toLocaleString()}` : menuItem.zh?.price
                },
                // 직접 필드도 업데이트
                name_ko: selectedLanguage === 'ko' ? menuName : (menuItem.name_ko || menuItem.ko?.name || ''),
                name_en: selectedLanguage === 'en' ? menuName : (menuItem.name_en || menuItem.en?.name || ''),
                name_ja: selectedLanguage === 'ja' ? menuName : (menuItem.name_ja || menuItem.ja?.name || ''),
                name_zh: selectedLanguage === 'zh' ? menuName : (menuItem.name_zh || menuItem.zh?.name || ''),
                description_ko: selectedLanguage === 'ko' ? menuDescription : (menuItem.description_ko || menuItem.ko?.description || ''),
                description_en: selectedLanguage === 'en' ? menuDescription : (menuItem.description_en || menuItem.en?.description || ''),
                description_ja: selectedLanguage === 'ja' ? menuDescription : (menuItem.description_ja || menuItem.ja?.description || ''),
                description_zh: selectedLanguage === 'zh' ? menuDescription : (menuItem.description_zh || menuItem.zh?.description || ''),
                // 기타 필요한 필드들
                options: menuItem.options,
                tags: Object.keys(selectedTags).filter(key => selectedTags[key]),
                soldOut: isSoldOut,
                basePrice: parseInt(priceNumber),
                hasOptions: menuItem.hasOptions,
                // 개별 태그 boolean 필드
                is_popular: selectedTags.popular,
                is_recommend: selectedTags.recommend,
                is_new: selectedTags.new,
                is_most_ordered: selectedTags.mostOrdered,
                // 판매시간 필드
                is_sales_time: isSalesTimeEnabled,
                start_time: isSalesTimeEnabled ? startTime : null,
                end_time: isSalesTimeEnabled ? endTime : null,
                openTime: isSalesTimeEnabled ? startTime : null,
                closeTime: isSalesTimeEnabled ? endTime : null,
                salesTime: isSalesTimeEnabled ? { start: startTime, end: endTime } : null
              };
              console.log('즉시 UI 업데이트:', immediateUpdate);
              onUpdate(immediateUpdate);
            }

            // DB 저장 및 이미지 처리
              // menuItemCode를 다시 확인 (menuItem.itemCode 또는 menuItem.menu_code 사용)
              const itemCodeToUpdate = menuItem?.itemCode || menuItem?.menu_code || menuItemCode;

              if (menuItem && menuItem.id) {
                // 기존 메뉴 업데이트
                console.log('Updating menu for language:', selectedLanguage);
                console.log('Menu name to update:', menuName);
                console.log('Menu description to update:', menuDescription);
                console.log('Current menu image:', menuImage);
                console.log('Original menu image:', originalImage);
                console.log('New image file:', newImageFile);

                // 이미지 처리 로직
                // 1. 새 이미지가 있는 경우 (교체 또는 신규 업로드)
                if (newImageFile) {
                  console.log('새 이미지 업로드 처리');
                  try {
                    const formData = new FormData();
                    formData.append('image', newImageFile);

                    // S3에 이미지 업로드 (storeId를 query param으로 전달)
                    const storeIdParam = storeInfo?.id || storeInfo?.store_id || '';
                    const uploadResponse = await fetch(`/api/menus/${menuItem.id}/image?storeId=${storeIdParam}`, {
                      method: 'POST',
                      headers: authHeaders(),
                      body: formData
                    });

                    if (!uploadResponse.ok) {
                      const error = await uploadResponse.json();
                      console.error('이미지 업로드 실패:', error.message);
                      // 페이지가 이미 닫혀있으므로 alert 사용하지 않음
                      return;
                    }

                    const uploadData = await uploadResponse.json();
                    console.log('이미지 업로드 성공:', uploadData.imageUrl);

                    // S3 업로드 성공 시에만 이미지 URL을 실제 URL로 교체
                    if (onUpdate && uploadData.imageUrl) {
                      // 이미지 URL만 업데이트 (나머지는 이미 즉시 업데이트에서 처리됨)
                      console.log('S3 업로드 완료 - 이미지 URL:', uploadData.imageUrl);

                      // 부분 업데이트를 위한 별도 콜백이 필요할 수 있음
                      // 현재는 이미지 URL 업데이트를 건너뜀 (즉시 업데이트에서 blob URL 사용 중)
                    }
                  } catch (error) {
                    console.error('이미지 업로드 오류:', error);
                    // 페이지가 이미 닫혀있으므로 alert 사용하지 않음
                    return;
                  }
                }
                // 2. 이미지가 삭제된 경우 (원래 이미지가 있었는데 현재 없고, 새 이미지도 없는 경우)
                else if (originalImage && !menuImage && !newImageFile) {
                  console.log('이미지 삭제 감지 - DB와 S3에서 삭제 처리');
                  console.log('Original image:', originalImage);
                  console.log('Current menuImage:', menuImage);
                  console.log('Menu ID for deletion:', menuItem.id);

                  const storeIdForDelete = storeInfo?.id || storeInfo?.store_id || '';
                  const deleteResponse = await fetch(`/api/menus/${menuItem.id}/image?storeId=${storeIdForDelete}`, {
                    method: 'DELETE',
                    headers: {
                      'Content-Type': 'application/json',
                      ...authHeaders()
                    }
                  });

                  if (!deleteResponse.ok) {
                    try {
                      const error = await deleteResponse.json();
                      console.error('이미지 삭제 실패:', error.message);
                    } catch {
                      console.error('이미지 삭제 API 호출 실패');
                    }
                    // 페이지가 이미 닫혀있으므로 alert 사용하지 않음
                    return;
                  }
                  console.log('이미지 삭제 성공');
                  // 삭제 성공 시 메뉴 객체의 image_url도 null로 업데이트
                  if (menuItem) {
                    menuItem.image_url = null;
                  }
                }

                // OKDC 매장인지 확인 (대소문자 구분 없이)
                const posType = (storeInfo?.pos_type || storeInfo?.posType || '').toLowerCase();
                const isOKDC = posType === 'okdc';
                console.log('[메뉴 수정] storeInfo:', storeInfo);
                console.log('[메뉴 수정] isOKDC:', isOKDC, 'pos_type:', posType);

                // 메뉴 ID 처리 (OKDC는 "menu-" 접두사 제거 필요)
                const actualMenuId = isOKDC && menuItem.id.startsWith('menu-')
                  ? menuItem.id.replace('menu-', '')
                  : menuItem.id;

                // 메뉴 업데이트 API 호출 (OKDC vs Union)
                const apiUrl = isOKDC
                  ? `/api/okdc/products/${actualMenuId}`
                  : `/api/menus/${menuItem.id}`;

                console.log('[메뉴 수정] API URL:', apiUrl, 'menuId:', menuItem.id, 'actualMenuId:', actualMenuId);

                const response = await fetch(apiUrl, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders()
                  },
                  body: JSON.stringify({
                    language: selectedLanguage,
                    storeId: storeInfo?.id || storeInfo?.store_id,   // METACITY 식별용 (store_id, prod_cd)
                    menuName: menuName,
                    description: menuDescription || '',
                    // wowpos 는 가격 편집 가능 매장 — price 를 실어야 wowpos_menus.price 가 저장된다.
                    // (OKDC 는 별도 엔드포인트, union/metacity 는 POS 관리라 price 미전송 유지)
                    ...(wpEditable ? { price: parseInt(String(menuPrice).replace(/[^0-9]/g, '')) || 0 } : {}),
                    isSoldout: isSoldOut,
                    isPopular: selectedTags.popular,
                    isRecommend: selectedTags.recommend,
                    isNew: selectedTags.new,
                    isMostOrdered: selectedTags.mostOrdered,
                    isSalesTime: isSalesTimeEnabled,
                    startTime: isSalesTimeEnabled ? startTime : null,
                    endTime: isSalesTimeEnabled ? endTime : null,
                    // 품절 자동 해제(다음 영업일 자동 재판매) — 품절 아니면 무의미하므로 false
                    autoResumeSoldout: isSoldOut ? autoResumeNextDay : false
                    // receiptYn 제거 — 상품별 출력 설정으로 이관. 백엔드는 receiptYn 미포함 시 기존값 보존.
                  })
                });

                const data = await response.json();

                if (!response.ok) {
                  console.error('번역 업데이트 실패:', data);
                  throw new Error('Failed to update translation');
                }

                console.log('번역 업데이트 성공:', selectedLanguage, data);

                // 옵션 번역 저장 (한국어가 아닌 경우에만)
                if (selectedLanguage !== 'ko' && optionsList.length > 0 && menuItem.options) {
                  console.log('옵션 번역 저장 시작');
                  console.log('optionsList:', optionsList);
                  console.log('menuItem.options:', menuItem.options);
                  console.log('isOKDC:', isOKDC);

                  // 각 옵션 그룹별로 번역 저장
                  for (let optIdx = 0; optIdx < optionsList.length; optIdx++) {
                    const option = optionsList[optIdx];
                    console.log('처리 중인 옵션:', option);

                    // 실제 DB의 옵션 그룹 찾기 - 인덱스 기반 매칭 우선
                    let dbOptionGroup = null;

                    // 1. originalIndex로 매칭 시도
                    if (option.originalIndex !== undefined && menuItem.options[option.originalIndex]) {
                      dbOptionGroup = menuItem.options[option.originalIndex];
                    }
                    // 2. option_group_id로 매칭 시도
                    if (!dbOptionGroup && option.option_group_id) {
                      dbOptionGroup = menuItem.options.find(opt => opt.option_group_id === option.option_group_id);
                    }
                    // 3. id로 매칭 시도
                    if (!dbOptionGroup) {
                      dbOptionGroup = menuItem.options.find(opt =>
                        opt.id === option.id ||
                        opt.group_name === option.name ||
                        opt.option_group_name === option.name
                      );
                    }

                    console.log('매칭된 DB 옵션 그룹:', dbOptionGroup);

                    if (dbOptionGroup) {
                      if (isOKDC) {
                        // OKDC 옵션 타입 확인 (attribute=SDA, side_dish=SDS)
                        const optionType = dbOptionGroup.option_type;
                        const isSDA = optionType === 'attribute';
                        const optionGroupId = dbOptionGroup.option_group_id || dbOptionGroup.sds_cls_cd || dbOptionGroup.sda_cls_cd;

                        console.log(`OKDC 옵션 그룹 ${optionGroupId} 번역 저장 - 타입: ${optionType}, 언어: ${selectedLanguage}, 이름: ${option.name}`);

                        // SDA와 SDS에 따라 다른 API 호출
                        console.log('[옵션 번역] storeInfo 전체:', storeInfo);
                        const okdcStoreId = storeInfo?.id || storeInfo?.store_id || menuItem?.store_id || '';
                        console.log('[옵션 번역] okdcStoreId:', okdcStoreId);
                        const groupApiUrl = isSDA
                          ? `/api/option-translations/okdc/sda/option-group/${okdcStoreId}/${encodeURIComponent(optionGroupId)}`
                          : `/api/option-translations/okdc/option-group/${okdcStoreId}/${encodeURIComponent(optionGroupId)}`;

                        const groupResponse = await fetch(groupApiUrl, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', ...authHeaders() },
                          body: JSON.stringify({
                            language: selectedLanguage,
                            name: option.name
                          })
                        });

                        if (!groupResponse.ok) {
                          console.error('OKDC 옵션 그룹 번역 저장 실패:', optionGroupId, await groupResponse.text());
                        } else {
                          console.log('OKDC 옵션 그룹 번역 저장 성공:', await groupResponse.json());
                        }

                        // OKDC 각 선택지 번역 저장
                        const dbItems = dbOptionGroup.items || [];
                        for (let choiceIdx = 0; choiceIdx < option.choices.length; choiceIdx++) {
                          const choice = option.choices[choiceIdx];
                          console.log('처리 중인 OKDC 선택지:', choice);

                          // 인덱스 기반 매칭 우선
                          let dbChoice = null;
                          if (choice.originalIndex !== undefined && dbItems[choice.originalIndex]) {
                            dbChoice = dbItems[choice.originalIndex];
                          }
                          if (!dbChoice && (choice.option_id || choice.sds_cd || choice.sda_cd)) {
                            dbChoice = dbItems.find(c =>
                              c.option_id === choice.option_id ||
                              c.sds_cd === choice.sds_cd ||
                              c.sda_cd === choice.sda_cd
                            );
                          }

                          console.log('매칭된 OKDC DB 선택지:', dbChoice);

                          if (dbChoice) {
                            const optionId = dbChoice.option_id || dbChoice.sds_cd || dbChoice.sda_cd;
                            console.log(`OKDC 선택지 ${optionId} 번역 저장 - 타입: ${optionType}, 언어: ${selectedLanguage}, 이름: ${choice.name}`);

                            // SDA와 SDS에 따라 다른 API 호출
                            const choiceApiUrl = isSDA
                              ? `/api/option-translations/okdc/sda/option/${okdcStoreId}/${encodeURIComponent(optionId)}`
                              : `/api/option-translations/okdc/option/${okdcStoreId}/${encodeURIComponent(optionId)}`;

                            const choiceResponse = await fetch(choiceApiUrl, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json', ...authHeaders() },
                              body: JSON.stringify({
                                language: selectedLanguage,
                                name: choice.name
                              })
                            });

                            if (!choiceResponse.ok) {
                              console.error('OKDC 옵션 선택지 번역 저장 실패:', optionId, await choiceResponse.text());
                            } else {
                              console.log('OKDC 선택지 번역 저장 성공:', await choiceResponse.json());
                            }
                          } else {
                            console.log('매칭되는 OKDC DB 선택지를 찾을 수 없음:', choice);
                          }
                        }
                      } else {
                        // Union POS 옵션 그룹명 번역 저장
                        // 토스는 그룹/선택지에 union 의 id 가 없다(키가 option_group_id / option_id).
                        // 폴백이 없으면 URL 에 undefined 가 들어가 저장이 통째로 실패한다.
                        const trGroupId = dbOptionGroup.id ?? dbOptionGroup.option_group_id;
                        console.log(`옵션 그룹 ${trGroupId} 번역 저장 - 언어: ${selectedLanguage}, 이름: ${option.name}`);

                        const groupResponse = await fetch(`/api/option-translations/option-group/${trGroupId}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', ...authHeaders() },
                          body: JSON.stringify({
                            language: selectedLanguage,
                            name: option.name,
                            // 토스 식별용 — 그룹 id 체계가 union 과 달라 서버가 매장으로 분기한다
                            storeId: storeInfo?.id || storeInfo?.store_id
                          })
                        });

                        if (!groupResponse.ok) {
                          console.error('옵션 그룹 번역 저장 실패:', trGroupId, await groupResponse.text());
                        } else {
                          console.log('옵션 그룹 번역 저장 성공:', await groupResponse.json());
                        }

                        // Union POS 각 선택지 번역 저장
                        for (const choice of option.choices) {
                          console.log('처리 중인 선택지:', choice);

                          // 토스는 선택지에 union 의 id 가 없어 화면 id 가 순번(1,2,3)이 된다.
                          // 이름까지 바꾼 선택지는 이름 매칭도 안 되므로 option_id 로 먼저 맞춘다.
                          const dbChoice = dbOptionGroup.choices?.find(c =>
                            (choice.option_id != null &&
                              (c.option_id === choice.option_id || c.id === choice.option_id)) ||
                            c.id === choice.id || c.option_id === choice.id ||
                            c.option_name === choice.name || c.name === choice.name
                          );

                          console.log('매칭된 DB 선택지:', dbChoice);

                          if (dbChoice) {
                            const trChoiceId = dbChoice.id ?? dbChoice.option_id;
                            console.log(`선택지 ${trChoiceId} 번역 저장 - 언어: ${selectedLanguage}, 이름: ${choice.name}`);

                            const choiceResponse = await fetch(`/api/option-translations/option/${trChoiceId}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json', ...authHeaders() },
                              body: JSON.stringify({
                                language: selectedLanguage,
                                name: choice.name,
                                storeId: storeInfo?.id || storeInfo?.store_id
                              })
                            });

                            if (!choiceResponse.ok) {
                              console.error('옵션 선택지 번역 저장 실패:', trChoiceId, await choiceResponse.text());
                            } else {
                              console.log('선택지 번역 저장 성공:', await choiceResponse.json());
                            }
                          } else {
                            console.log('매칭되는 DB 선택지를 찾을 수 없음:', choice);
                          }
                        }
                      }
                    } else {
                      console.log('매칭되는 DB 옵션 그룹을 찾을 수 없음:', option);
                    }
                  }
                  console.log('옵션 번역 저장 완료');
                }

                // === WowPos 옵션 저장 ===
                // ko 세션: 구조 전체 교체(그룹/선택지/가격/단일다중). en/ja/zh 세션: 번역만(id 별 PUT).
                if (wpEditable && menuItem?.id) {
                  const wpMenuId = menuItem.db_id || (typeof menuItem.id === 'string' ? menuItem.id.replace(/^menu-/, '') : menuItem.id);
                  try {
                    if (selectedLanguage === 'ko') {
                      await fetch(`/api/wowpos/menus/${wpMenuId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', ...authHeaders() },
                        body: JSON.stringify({ options: buildWowposOptionsPayload() }),
                      });
                    } else {
                      for (const opt of optionsList) {
                        const gid = opt.id;
                        if (gid && !String(gid).startsWith('new-') && (opt.name || '').trim()) {
                          await fetch(`/api/option-translations/wowpos/option-group/${gid}`, {
                            method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                            body: JSON.stringify({ language: selectedLanguage, name: opt.name }),
                          });
                        }
                        for (const ch of (opt.choices || [])) {
                          if (ch.id && !String(ch.id).startsWith('new-') && (ch.name || '').trim()) {
                            await fetch(`/api/option-translations/wowpos/option/${ch.id}`, {
                              method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                              body: JSON.stringify({ language: selectedLanguage, name: ch.name }),
                            });
                          }
                        }
                      }
                    }
                  } catch (wpOptErr) {
                    console.error('[WowPos 옵션 저장] 실패:', wpOptErr);
                  }
                }

                // localStorage에 메뉴 업데이트 알림
                localStorage.setItem('menuUpdated', Date.now().toString());

                // 백그라운드 처리에서는 onUpdate를 다시 호출하지 않음
                // 이미 UI는 즉시 업데이트되었고, S3 업로드 완료 시에만 추가 업데이트
                console.log('DB 업데이트 완료 - UI는 이미 업데이트됨');
              } else {
                console.log('메뉴 번역 업데이트 건너뜀 - menuItem:', !!menuItem, 'itemCode:', itemCodeToUpdate);

                // 새 메뉴 추가인 경우 onAddMenu 호출
                if (onAddMenu) {
                  console.log('onAddMenu 함수 호출');
                  onAddMenu(category, newMenuItem);
                }
              }

              // 모든 처리 완료 후 부드러운 애니메이션으로 페이지 닫기
              console.log('모든 처리 완료 - 페이지 닫기');
              setIsProcessing(false);

              // 판매시간 변경(dirty) + 수정 모드면 일괄 적용 모달 노출 (onClose 보류)
              // dirty 는 onUpdate 전 스냅샷을 사용 (initialSalesTimeRef 가 덮어써져도 안전)
              const isEditMode = !!(menuItem && menuItem.id);
              const curST = _salesCurSnapshot;
              const salesTimeDirty = _salesTimeDirtySnapshot;

              // 닫기 애니메이션 시작
              setIsAnimating(false);
              setCurrentY(window.innerHeight);

              setTimeout(() => {
                // dirty 면 부모에 일괄 적용 모달 요청 (부모 콜백이라 언마운트돼도 실행됨)
                // 품절/자동해제 변경을 우선 (둘 다 바뀐 경우 모달 이중 표시 방지)
                if (isEditMode && _soldOutDirtySnapshot && onRequestBulkSoldOut) {
                  onRequestBulkSoldOut({
                    is_soldout: _soldOutCurSnapshot.is_soldout,
                    auto_resume_soldout: _soldOutCurSnapshot.auto_resume_soldout,
                  });
                } else if (isEditMode && salesTimeDirty && onRequestBulkSalesTime) {
                  onRequestBulkSalesTime({
                    is_sales_time: curST.is_sales_time,
                    start_time: curST.is_sales_time ? curST.start_time : null,
                    end_time: curST.is_sales_time ? curST.end_time : null,
                  });
                }
                onClose();
              }, 300);

              } catch (error) {
                console.error('DB 저장 실패:', error);
                setIsProcessing(false);
                // 에러 발생 시에도 애니메이션으로 페이지 닫기
                setIsAnimating(false);
                setCurrentY(window.innerHeight);
                setTimeout(() => {
                  onClose();
                }, 300);
              }
            };

            // 비동기 처리 실행
            processUpdate();
          }}
          disabled={isProcessing}
          style={{ opacity: isProcessing ? 0.6 : 1 }}>
            {isProcessing ? '처리 중...' : '저장'}
          </button>
        </div>
        </div>
      </div>
      
      {/* 삭제 확인 팝업 */}
      {showDeleteConfirm && (
        <div 
          className={`delete-confirm-overlay ${deletePopupAnimating ? 'show' : ''}`} 
          onClick={() => {
            setDeletePopupAnimating(false);
            setTimeout(() => setShowDeleteConfirm(false), 200);
          }}
        >
          <div 
            className={`delete-confirm-popup ${deletePopupAnimating ? 'show' : ''}`} 
            onClick={(e) => e.stopPropagation()}
            style={{
              zoom: window.innerWidth > 540 ? 1.5 : Math.min(window.innerWidth / 360, 1.5)
            }}
          >
            <div className="delete-confirm-text">
              '{menuItem?.ko?.name || menuItem?.name || '메뉴'}' 를<br />
              정말 삭제하시겠습니까?
            </div>
            <div className="delete-confirm-buttons">
              <button 
                className="delete-cancel-button"
                onClick={() => {
                  setDeletePopupAnimating(false);
                  setTimeout(() => setShowDeleteConfirm(false), 200);
                }}
              >
                취소
              </button>
              <button 
                className="delete-confirm-button"
                onClick={() => {
                  // 먼저 애니메이션 시작
                  setDeletePopupAnimating(false);
                  
                  // 애니메이션이 끝난 후 팝업 닫고 삭제 실행
                  setTimeout(() => {
                    setShowDeleteConfirm(false);
                    // 팝업이 완전히 닫힌 후 삭제 실행 및 시트 닫기
                    setTimeout(() => {
                      if (onDeleteMenu) {
                        onDeleteMenu(category, menuItem.id, false); // false로 변경 - 여기서 직접 닫기
                      }
                      // 삭제 후 시트 닫기
                      onClose();
                    }, 50);
                  }, 200);
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .menu-edit-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0);
          z-index: 10000;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          transition: background-color 0.3s ease;
          pointer-events: none;
          overflow: hidden;
          overscroll-behavior: none;
          touch-action: none;
        }
        
        .menu-edit-overlay.open {
          background-color: rgba(0, 0, 0, 0.5);
          pointer-events: auto;
        }
        
        .menu-edit-sheet {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          width: 100%;
          max-width: 768px;
          margin: 0 auto;
          height: 90vh;
          height: calc(var(--vh, 1vh) * 90);
          background-color: transparent;
          transform: translateY(100%);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: auto;
        }

        .menu-edit-sheet.open {
          transform: translateY(0);
        }
        
        .menu-edit-wrapper {
          width: 360px;
          height: 100%;
          margin: 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          transform-origin: top center;
          background: white;
          border-radius: 24px 24px 0 0;
          overflow: hidden;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          overscroll-behavior: none;
          touch-action: none;
        }
        
        @media screen and (min-width: 361px) {
          .menu-edit-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .menu-edit-wrapper {
            zoom: 1.5;
          }
        }
        
        @media screen and (max-width: 360px) {
          .menu-edit-wrapper {
            width: 360px;
            zoom: calc(100vw / 360);
          }
        }
        
        .drag-handle-area {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 48px;
          cursor: grab;
          touch-action: none;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          z-index: 1;
          padding-top: 12px;
          box-sizing: border-box;
        }

        .drag-handle-area:active {
          cursor: grabbing;
        }

        .drag-handle {
          width: 52px;
          height: 4px;
          background: #E5E8EB;
          border-radius: 10px;
          transition: all 0.2s ease;
        }
        
        .menu-edit-sheet.open .drag-handle {
          background: #E5E8EB;
        }
        
        .drag-handle-area:active .drag-handle {
          background: #C9CDD1;
          transform: scaleX(1.2);
        }

        .header-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          border-bottom: 1px solid #F3F5F7;
          padding-bottom: 24px;
          padding-top: 40px;
        }

        .header-title {
          font-size: 18px;
          font-weight: 600;
          color: #2E2E2E;
          margin: 0;
          text-align: center;
        }

        .content {
          flex: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
          overscroll-behavior: none;
          touch-action: pan-y;
          padding-bottom: 240px;
          position: relative;
          z-index: 1;
        }

        .content::-webkit-scrollbar {
          width: 6px;
        }
        
        .content::-webkit-scrollbar-track {
          background: #F5F5F5;
          border-radius: 10px;
        }
        
        .content::-webkit-scrollbar-thumb {
          background: #CCCCCC;
          border-radius: 10px;
        }
        
        .content::-webkit-scrollbar-thumb:hover {
          background: #AAAAAA;
        }

        .form-container {
          display: flex;
          flex-direction: column;
          padding: 24px 20px 20px 20px;
          border-bottom: 1px solid #F3F5F7;
          gap: 0;
        }
        
        /* 메뉴 기본 정보 그룹 */
        .menu-basic-info-group {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .image-upload-container {
          position: relative;
          width: 120px;
          height: 120px;
        }

        .image-upload-field {
          width: 120px;
          height: 120px;
          border: none;
          border-radius: 12px;
          background-color: #FAFAFC;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          position: relative;
          overflow: hidden;
          box-sizing: border-box;
        }

        .image-delete-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 20px;
          height: 20px;
          background: transparent;
          border: none;
          cursor: pointer;
          z-index: 100;
          transition: transform 0.1s ease;
          padding: 0;
        }

        .image-delete-btn .delete-icon {
          width: 20px;
          height: 20px;
          object-fit: contain;
          display: block;
        }

        .image-upload-field.has-image {
          background-color: transparent;
        }

        .upload-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .upload-placeholder span {
          font-size: 12px;
          font-weight: 500;
          color: #CBCBCB;
        }

        .uploaded-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .input-fields {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 16px;
          justify-content: center;
        }

        .menu-name-input,
        .menu-price-input {
          width: 100%;
          height: 51px;
          border: 1px solid #E8E8E8;
          border-radius: 8px;
          padding: 0 16px;
          font-size: 16px;
          font-weight: 500;
          color: #535353;
          background-color: transparent;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          transition: all 0.2s ease;
          outline: none;
        }

        .menu-name-input::placeholder,
        .menu-price-input::placeholder {
          color: #CBCBCB;
        }

        .menu-name-input.has-value:not(.focused),
        .menu-price-input.has-value:not(.focused) {
          background-color: #FAFAFB;
        }

        .menu-name-input.focused,
        .menu-price-input.focused {
          border-color: #FDCD27;
          caret-color: #FDCD27;
        }
        
        .menu-name-input.error,
        .menu-price-input.error {
          border-color: #FF4444 !important;
          border-width: 1px !important;
          animation: shake 0.4s ease-in-out;
        }
        
        .menu-name-input.error.focused,
        .menu-price-input.error.focused {
          border-color: #FDCD27 !important;
          border-width: 1px !important;
        }

        /* 읽기 전용 필드 스타일 */
        .menu-name-input.readonly,
        .menu-price-input.readonly,
        .choice-name-input.readonly,
        .choice-price-input.readonly,
        .option-name-input.readonly {
          background-color: #F5F5F5 !important;
          color: #999999 !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
        }

        .menu-price-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .menu-price-input {
          padding-right: 40px;
        }

        .currency {
          position: absolute;
          right: 16px;
          font-size: 16px;
          font-weight: 500;
          color: #999999;
        }
        
        .tag-box {
          padding: 20px 26px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #F3F5F7;
        }
        
        .tag-label {
          font-size: 16px;
          font-weight: 600;
          color: #333333;
        }
        
        .tag-buttons {
          display: flex;
          gap: 6px;
        }
        
        .tag-button {
          height: 31px;
          padding: 8px 12px;
          border-radius: 16px;
          background-color: #F5F5F5;
          border: none;
          font-size: 14px;
          font-weight: 500;
          color: #999999;
          cursor: pointer;
          white-space: nowrap;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s ease-out;
          position: relative;
          overflow: hidden;
        }
        
        .tag-button:active {
          transform: scale(0.95);
        }
        
        .tag-button.active {
          animation: tagFillBackground 0.2s ease-out forwards;
          color: #FFFFFF;
        }
        
        .tag-button.active::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background-color: #FDCD27;
          transform: translate(-50%, -50%);
          animation: tagRipple 0.3s ease-out;
          z-index: -1;
        }
        
        @keyframes tagFillBackground {
          0% {
            background-color: #F5F5F5;
            transform: scale(1);
          }
          50% {
            transform: scale(0.95);
          }
          100% {
            background-color: #FDCD27;
            transform: scale(1);
          }
        }
        
        @keyframes tagRipple {
          0% {
            width: 0;
            height: 0;
            opacity: 1;
          }
          100% {
            width: 200%;
            height: 200%;
            opacity: 0;
          }
        }
        
        .soldout-box {
          border-bottom: 1px solid #F3F5F7;
          overflow: hidden;
        }

        .soldout-header {
          padding: 20px 26px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
        }

        .soldout-label {
          font-size: 16px;
          font-weight: 600;
          color: #333333;
        }

        .soldout-detail-wrapper {
          overflow: hidden;
          transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 0.3s ease-out;
        }

        .soldout-detail-wrapper.collapsed {
          max-height: 0;
          opacity: 0;
        }

        /* max-height 를 실제 콘텐츠 높이에 맞게 좁힘 — 컨테이너 펼침과 텍스트 슬라이드가
           같이 끝나도록(여유가 크면 컨테이너만 먼저 다 펼쳐지고 텍스트가 늦게 내려옴) */
        .soldout-detail-wrapper.expanded {
          max-height: 44px;
          opacity: 1;
        }

        .soldout-resume-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 26px 19px 26px;
          cursor: pointer;
          transform: translateY(0);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }

        .soldout-detail-wrapper.collapsed .soldout-resume-row {
          transform: translateY(-10px);
        }

        /* 라벨 폰트 = 주문페이지 옵션명(.option-name)과 동일 */
        .soldout-resume-label {
          font-size: 14px;
          font-weight: 400;
          color: #000000;
        }

        /* 체크박스 = 주문페이지 옵션 체크박스와 동일 (SVG 마스크 애니메이션) */
        .option-checkbox {
          width: 19px;
          height: 19px;
          flex-shrink: 0;
        }

        .checkbox-wrapper {
          width: 19px;
          height: 19px;
          position: relative;
          overflow: hidden;
        }

        .checkbox-wrapper.animating {
          animation: soldoutCheckboxScale 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes soldoutCheckboxScale {
          0% { transform: scale(1); }
          40% { transform: scale(0.9); }
          100% { transform: scale(1); }
        }

        .checkbox-wrapper svg {
          overflow: visible;
        }

        .checkbox-border {
          opacity: 1;
          transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .checkbox-white {
          transform: scale(1);
          transform-origin: center;
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .checkbox-mask-rect {
          transform: translateX(0);
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1) 0.1s;
        }

        .checkbox-mark {
          opacity: 1;
        }

        .checkbox-wrapper.selected .checkbox-border {
          opacity: 0;
        }

        .checkbox-wrapper.selected .checkbox-white {
          transform: scale(0);
        }

        .checkbox-wrapper.selected .checkbox-mask-rect {
          transform: translateX(19px);
        }

        .checkbox-wrapper:not(.selected) .checkbox-mask-rect {
          transform: translateX(0);
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .toggle-switch {
          width: 48px;
          height: 28px;
          border-radius: 28px;
          background-color: #DEE2E6;
          position: relative;
          cursor: pointer;
          transition: background-color 0.2s ease, transform 0.1s ease;
        }

        .toggle-switch:active {
          transform: scale(0.9);
        }
        
        .toggle-switch.on {
          background-color: #FDCD27;
        }
        
        .toggle-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background-color: #FFFFFF;
          position: absolute;
          top: 2px;
          left: 2px;
          cursor: grab;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
        }
        
        .toggle-thumb:active {
          cursor: grabbing;
        }
        
        .sales-time-box {
          border-bottom: 1px solid #F3F5F7;
          overflow: hidden;
        }

        /* 메뉴 삭제 버튼 (WowPos 매장 수정 모드 — Step 2) */
        .menu-delete-btn {
          display: block;
          width: calc(100% - 52px);
          margin: 18px 26px 8px;
          padding: 14px 0;
          background-color: #FFF1F0;
          color: #E14B4B;
          border: 1px solid #FFCFCB;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transition: background-color 0.12s ease, transform 0.12s ease;
        }
        .menu-delete-btn:active {
          background-color: #FFE2E0;
          transform: scale(0.98);
        }
        
        .sales-time-header {
          padding: 20px 26px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
        }
        
        .sales-time-label {
          font-size: 16px;
          font-weight: 600;
          color: #333333;
        }
        
        .time-input-wrapper {
          overflow: hidden;
          transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
                      opacity 0.3s ease-out;
        }
        
        .time-input-wrapper.collapsed {
          max-height: 0;
          opacity: 0;
        }
        
        .time-input-wrapper.expanded {
          max-height: 88px;
          opacity: 1;
        }
        
        .time-input-container {
          padding: 0 26px 19px 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8.5px;
          transform: translateY(0);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .time-input-wrapper.collapsed .time-input-container {
          transform: translateY(-10px);
        }
        
        .time-input {
          width: 139px;
          height: 50px;
          border: 1px solid #E8E8E8;
          border-radius: 8px;
          padding: 0 16px;
          font-size: 18px;
          font-weight: 500;
          color: #535353;
          background-color: transparent;
          outline: none;
          text-align: center;
          box-sizing: border-box;
        }
        
        .time-input::placeholder {
          color: #CBCBCB;
        }
        
        .time-input.has-value:not(.focused) {
          background-color: #FAFAFB;
        }
        
        .time-input.focused {
          border-color: #FDCD27;
          caret-color: #FDCD27;
        }
        
        .time-input.error {
          border: 1px solid #FF4444;
          animation: shake 0.4s ease-in-out;
        }
        
        .time-input.error:focus {
          border: 1px solid #FDCD27;
          animation: none;
        }
        
        .time-separator {
          font-size: 20px;
          font-weight: 500;
          color: #CBCBCB;
        }

        .bottom-actions {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 100px;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          padding-bottom: 16px;
          border-top: none;
          gap: 8px;
          z-index: 10;
          pointer-events: none;
        }

        .bottom-actions::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: url(${bottomNavBg});
          background-size: cover;
          background-position: center;
          pointer-events: none;
          z-index: 0;
        }

        .bottom-actions .action-button {
          pointer-events: auto;
          position: relative;
          z-index: 2;
        }

        .action-button {
          width: 164px;
          height: 50px;
          border-radius: 10px;
          border: none;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.1s ease;
        }
        
        .action-button.cancel-button {
          background: #F1F1F1;
          color: #959595;
        }
        
        .action-button.add-button {
          background: #FDCD27;
          color: #FFFFFF;
        }
        
        .action-button:active {
          animation: buttonPress 0.1s ease-out;
        }



        @keyframes buttonPress {
          0% {
            transform: scale(1);
          }
          40% {
            transform: scale(0.92);
          }
          100% {
            transform: scale(1);
          }
        }

        /* 점선 테두리를 위한 ::after 사용 */
        .image-upload-field::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 12px;
          border: 1px dashed #D1D1D1;
          pointer-events: none;
          z-index: 1;
        }
        
        .image-upload-field.has-image::after {
          border-style: solid;
          border-color: #EAEAEA;
        }
        
        /* 메뉴 설명 입력 필드 */
        .menu-description-input {
          display: block;
          width: 320px;
          min-height: 100px;
          margin-top: 16px;
          margin-left: 0;
          padding: 15px;
          border: 1px solid #E8E8E8;
          border-radius: 8px;
          background: #FFFFFF;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          font-size: 16px;
          font-weight: 500;
          color: #2E2E2E;
          resize: none;
          outline: none;
          transition: all 0.2s ease;
          box-sizing: border-box;
          caret-color: #FDCD27;
        }
        
        .menu-description-input::placeholder {
          color: #CBCBCB;
          font-weight: 400;
        }
        
        .menu-description-input:focus {
          border-color: #FDCD27;
        }
        
        .menu-description-input.has-value:not(.focused) {
          background: #FAFAFB;
        }
        
        /* 스크롤바 스타일 */
        .menu-description-input::-webkit-scrollbar {
          width: 6px;
        }
        
        .menu-description-input::-webkit-scrollbar-track {
          background: transparent;
          margin: 4px 0;
        }
        
        .menu-description-input::-webkit-scrollbar-thumb {
          background: #DDDDDD;
          border-radius: 10px;
        }
        
        .menu-description-input::-webkit-scrollbar-thumb:hover {
          background: #BBBBBB;
        }
        
        /* 옵션 박스 스타일 */
        .option-box {
          padding: 20px 16px;
          display: flex;
          flex-direction: column;
          border-bottom: 1px solid #F3F5F7;
        }
        
        .option-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0;
        }
        .option-header.expanded {
          margin-bottom: 20px;
        }
        
        .option-box-label {
          font-size: 16px;
          font-weight: 600;
          color: #2E2E2E;
          margin-left: 10px;
        }

        /* WowPos 옵션 편집 버튼 */
        .wp-opt-add-group {
          background-color: #f1f1f1;
          color: #2f2f2f;
          border: none;
          border-radius: 8px;
          width: 28px;
          height: 28px;
          font-size: 20px;
          font-weight: 500;
          line-height: 1;
          cursor: pointer;
          padding: 0 0 2px 0;
          margin-right: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transition: transform 0.12s ease, background-color 0.12s ease;
        }
        .wp-opt-add-group:active {
          background-color: #e8e8e8;
          transform: scale(0.94);
        }
        .wp-choice-del {
          flex-shrink: 0;
          margin-left: 6px;
          width: 28px;
          height: 28px;
          background: transparent;
          border: none;
          line-height: 1;
          cursor: pointer;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .wp-choice-del-icon {
          width: 16px;
          height: 16px;
          object-fit: contain;
          display: block;
        }
        .wp-choice-add {
          margin-top: 12px;
          width: 100%;
          background: #fff;
          color: #2E2E2E;
          border: 1px dashed #cbcbcb;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
        }
        .wp-group-del {
          display: block;
          width: 100%;
          margin-top: 12px;
          padding: 10px 0;
          background-color: #FFF1F0;
          color: #E14B4B;
          border: 1px solid #FFCFCB;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transition: background-color 0.12s ease, transform 0.12s ease;
        }
        .wp-group-del:active {
          background-color: #FFE2E0;
          transform: scale(0.98);
        }

        .delete-toggle {
          font-size: 14px;
          font-weight: 600;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0 2px 0 0;
          text-decoration: underline;
          margin-right: 12px;
        }
        
        .delete-toggle:not(.active) {
          color: #FF4E4E;
        }
        
        .delete-toggle.active {
          color: #999999;
        }
        
        .option-items-container {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        
        /* 옵션 상세 박스 */
        .option-detail-box {
          width: 328px;
          padding: 28px 16px 20px 16px;
          border: 1px solid #EEEEEE;
          border-radius: 12px;
          background: #FFFFFF;
          position: relative;
          transition: all 0.3s ease;
        }
        
        .option-detail-box.collapsed {
          height: 61px;
          padding: 20px 16px;
          overflow: hidden;
        }
        
        /* 확장/축소 아이콘 */
        .expand-icon {
          position: absolute;
          top: 25px;
          right: 16px;
          width: 16px;
          height: 9px;
          cursor: pointer;
          z-index: 1;
        }
        
        .expand-icon.expanded {
          transform: rotate(180deg);
        }
        
        /* 삭제 아이콘 */
        .delete-icon-option {
          position: absolute;
          top: 20px;
          right: 16px;
          width: 20px;
          height: 20px;
          cursor: pointer;
          z-index: 1;
        }
        
        /* 축소 상태 옵션 이름 */
        .collapsed-option-name {
          position: absolute;
          left: 24px;
          top: 20px;
          right: 50px;
          font-size: 16px;
          font-weight: 600;
          color: #000000;
          z-index: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        /* 옵션 이름 섹션 */
        .option-name-section {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .option-label {
          font-size: 14px;
          font-weight: 600;
          color: #2E2E2E;
        }
        
        .option-name-input {
          width: 296px;
          height: 49px;
          padding: 0 16px;
          border: 1px solid #E8E8E8;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #2E2E2E;
          outline: none;
          caret-color: #FDCD27;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          background-color: transparent;
          transition: all 0.2s ease;
        }
        
        .option-name-input::placeholder {
          font-weight: 500;
        }
        
        .option-name-input.has-value:not(.focused) {
          background-color: #FAFAFB;
        }
        
        .option-name-input:focus,
        .option-name-input.focused {
          border-color: #FDCD27;
        }
        
        .option-name-input.error {
          border-color: #FF4444 !important;
          border-width: 1px !important;
          animation: shake 0.4s ease-in-out;
        }
        
        .option-name-input.error:focus,
        .option-name-input.error.focused {
          border-color: #FDCD27 !important;
          border-width: 1px !important;
        }
        
        @keyframes shake {
          0% { transform: translateX(0); }
          12.5% { transform: translateX(-1px); }
          25% { transform: translateX(1px); }
          37.5% { transform: translateX(-1px); }
          50% { transform: translateX(1px); }
          62.5% { transform: translateX(-1px); }
          75% { transform: translateX(1px); }
          87.5% { transform: translateX(-1px); }
          100% { transform: translateX(0); }
        }
        
        .option-name-input::placeholder {
          color: #B8B8B8;
        }
        
        /* 필수 옵션 섹션 */
        .required-option-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 30px;
        }
        
        .required-option-label {
          font-size: 14px;
          font-weight: 600;
          color: #2E2E2E;
        }
        
        /* 선택지 섹션 */
        .choices-section {
          margin-top: 30px;
        }
        
        .choices-label {
          font-size: 14px;
          font-weight: 600;
          color: #2E2E2E;
        }
        
        .choices-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top:12px;
        }
        
        .choice-item {
          display: flex;
          align-items: center;
          gap: 8px;
          opacity: 1;
          transform: translateX(0);
          height: 49px;
          overflow: visible;
          position: relative;
          width: 100%;
        }
        
        .choice-item.deleting {
          opacity: 0;
          transform: translateX(-100%);
          height: 0;
          margin-bottom: 0;
          overflow: hidden;
        }
        
        .choice-name-input {
          flex: 1;
          min-width: 0;
          height: 49px;
          padding: 0 12px;
          border: 1px solid #E8E8E8;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #2E2E2E;
          outline: none;
          caret-color: #FDCD27;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          background-color: transparent;
          transition: border-color 0.2s ease, background-color 0.2s ease;
        }
        
        .choice-name-input::placeholder {
          font-weight: 500;
        }
        
        .choice-name-input.has-value:not(.focused) {
          background-color: #FAFAFB;
        }
        
        .choice-name-input:focus,
        .choice-name-input.focused {
          border-color: #FDCD27;
        }
        
        .choice-name-input.error {
          border-color: #FF4444 !important;
          border-width: 1px !important;
          animation: shake 0.4s ease-in-out;
        }
        
        .choice-name-input.error:focus,
        .choice-name-input.error.focused {
          border-color: #FDCD27 !important;
          border-width: 1px !important;
        }
        
        .choice-name-input::placeholder {
          color: #B8B8B8;
        }
        
        
        .choice-price-wrapper {
          position: relative;
          width: 106px;
          height: 49px;
          display: flex;
          align-items: center;
          border: 1px solid #E8E8E8;
          border-radius: 8px;
          background: white;
          overflow: hidden;
          transition: border-color 0.2s ease, border-width 0.2s ease;
          flex-shrink: 0;
        }
        
        .choice-price-wrapper:focus-within {
          border-color: #FDCD27;
        }
        
        .choice-price-wrapper.has-value:not(:focus-within) {
          background-color: #FAFAFB;
        }
        
        .choice-price-wrapper.error {
          border-color: #FF4444 !important;
          border-width: 1px !important;
          animation: shake 0.4s ease-in-out;
        }
        
        .choice-price-wrapper.error.focused {
          border-color: #FDCD27 !important;
          border-width: 1px !important;
        }
        
        .price-plus {
          position: absolute;
          top: 14px;
          left: 12px;
          font-size: 14px;
          font-weight: 500;
          color: #CBCBCB;
          pointer-events: none;
        }
        
        .choice-price-input {
          width: 100%;
          height: 100%;
          padding: 0 24px 0 24px;
          border: none;
          background: transparent;
          font-size: 14px;
          font-weight: 600;
          color: #2E2E2E;
          outline: none;
          text-align: left;
          caret-color: #FDCD27;
          font-variant-numeric: tabular-nums;
        }
        
        .choice-price-input::placeholder {
          color: #B8B8B8;
          font-weight: 500;
        }
        
        .price-won {
          position: absolute;
          right: 12px;
          font-size: 14px;
          font-weight: 500;
          color: #666666;
          pointer-events: none;
        }
        
        .delete-choice-btn {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          margin-left: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .delete-icon {
          width: 20px;
          height: 20px;
          }
        
        .add-choice-btn {
          width: 100%;
          height: 40px;
          margin-top: 4px;
          background: transparent;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #666666;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          transition: transform 0.1s ease, background-color 0.1s ease;
        }
        
        .add-choice-btn:active {
          transform: scale(0.95);
          background-color: #F5F5F5;
        }
        
        .divider-line {
          width: calc(100% + 44px);
          height: 1px;
          background-color: #F3F5F7;
        }
        
        .delete-menu-button {
          width: 328px;
          height: 50px;
          background-color: #FFDEDE;
          color: #FF3838;
          border: none;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .delete-menu-button:active {
          animation: buttonPress 0.1s ease-out;
        }
        
        
        .add-option-button {
          width: 328px;
          height: 59px;
          background: #FFFFFF;
          border: 2px dashed #DEDEDE;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          cursor: pointer;
          padding: 0;
          transition: background-color 0.2s ease;
          -webkit-tap-highlight-color: transparent;
          -webkit-touch-callout: none;
        }
        
        .plus-icon {
          width: 12px;
          height: 12px;
        }
        
        .add-option-button span {
          font-size: 16px;
          font-weight: 500;
          color: #959595;
        }
        
        /* 삭제 확인 팝업 스타일 */
        .delete-confirm-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: rgba(0, 0, 0, 0);
          z-index: 20000;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding-top: 250px;
          transition: background-color 0.2s ease;
        }
        
        .delete-confirm-overlay.show {
          background-color: rgba(0, 0, 0, 0.5);
        }
        
        .delete-confirm-popup {
          width: 320px;
          background: #FFFFFF;
          border-radius: 16px;
          padding: 30px 26px 24px 26px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
        }
        
        .delete-confirm-popup.show {
          opacity: 1;
          transform: translateY(0);
        }
        
        .delete-confirm-text {
          font-size: 20px;
          font-weight: bold;
          line-height: 28px;
          text-align: left;
          color: #2E2E2E;
          margin-bottom: 20px;
        }
        
        .delete-confirm-buttons {
          display: flex;
          gap: 8px;
        }
        
        .delete-cancel-button,
        .delete-confirm-button {
          flex: 1;
          height: 48px;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.1s ease;
        }
        
        .delete-cancel-button {
          background: #F1F1F1;
          color: #959595;
        }
        
        .delete-confirm-button {
          background: #FFDEDE;
          color: #FF3838;
        }
        
        .delete-cancel-button:active,
        .delete-confirm-button:active {
          transform: scale(0.95);
        }
      `}</style>
    </div>
  );
};

export default MenuEditBottomSheet;